// src/activities.ts
import { Type as Type2 } from "@sinclair/typebox";

// src/common.ts
import { Type } from "@sinclair/typebox";
var PHONE_E164_PATTERN = "^\\+[1-9][0-9]{7,14}$";
var PHONE_PATTERN = "^\\+(?:91[0-9]{10}|(?!91)[1-9][0-9]{7,14})$";
var PhoneE164 = Type.String({
  pattern: PHONE_E164_PATTERN,
  description: "E.164 phone number, e.g. +919876543210"
});
var PhoneE164Input = Type.String({
  pattern: PHONE_PATTERN,
  description: "E.164 phone number, e.g. +919876543210"
});
var PATTERN_HINTS = {
  phoneE164: "must be a phone number, e.g. +919876543210 \u2014 Indian numbers are ten digits",
  altPhoneE164: "must be a phone number, e.g. +919876543210 \u2014 Indian numbers are ten digits",
  key: "must be lowercase letters, numbers and dashes, e.g. new-sellers"
};
var PageQuery = Type.Object({
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 25 })),
  offset: Type.Optional(Type.Integer({ minimum: 0, default: 0 }))
});
var pageOf = (item) => Type.Object({
  items: Type.Array(item),
  total: Type.Integer(),
  limit: Type.Integer(),
  offset: Type.Integer()
});
var DateOnly = Type.String({ format: "date", description: "YYYY-MM-DD" });
var Nullable = (schema) => Type.Union([schema, Type.Null()]);
var enumOf = (values) => Type.Union(values.map((v) => Type.Literal(v)));
var FOLLOW_UP_TYPES = ["call", "whatsapp", "meeting", "demo", "discussion", "other"];
var FOLLOW_UP_OVERLAPPING_TYPES = ["whatsapp", "other"];
function occupiesSlot(type) {
  return !FOLLOW_UP_OVERLAPPING_TYPES.includes(type);
}
var FOLLOW_UP_PRIORITIES = ["high", "normal", "low"];
var DEFAULT_DIALLING_CODE = "91";
var NATIONAL_NUMBER_LENGTHS = {
  91: 10
  // India: mobiles and landlines are both ten digits.
};
function isValidE164(value) {
  return /^\+[1-9][0-9]{7,14}$/.test(value);
}
function accept(e164) {
  if (!isValidE164(e164)) return null;
  const digits = e164.slice(1);
  for (const [code, length] of Object.entries(NATIONAL_NUMBER_LENGTHS)) {
    if (digits.startsWith(code)) return digits.length === code.length + length ? e164 : null;
  }
  return e164;
}
function normalisePhone(input, defaultCode = DEFAULT_DIALLING_CODE) {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/[^\d\s+()./-]/.test(trimmed)) return null;
  const hadPlus = trimmed.startsWith("+");
  let digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;
  if (hadPlus) return accept(`+${digits}`);
  if (digits.startsWith("00")) {
    digits = digits.slice(2);
    return accept(`+${digits}`);
  }
  digits = digits.replace(/^0+/, "");
  if (!digits) return null;
  const national = NATIONAL_NUMBER_LENGTHS[defaultCode];
  if (digits.startsWith(defaultCode) && (national ? digits.length === defaultCode.length + national : digits.length > 10)) {
    return accept(`+${digits}`);
  }
  return accept(`+${defaultCode}${digits}`);
}

// src/activities.ts
var ACTIVITY_TYPES = [
  "call_logged",
  "note_added",
  "tag_added",
  "tag_removed",
  "lead_created",
  "lead_status_changed",
  "lead_converted",
  "deal_created",
  "deal_stage_changed",
  "deal_won",
  "deal_lost",
  "account_status_changed",
  "record_updated",
  "whatsapp_template_sent",
  "whatsapp_direct_sent",
  "whatsapp_status_changed",
  /**
   * Phase 2. Click-to-chat could not see a reply at all, so these three had
   * no source: the customer's own message, the CRM's automatic answer, and
   * the rating that answer produced.
   *
   * The DB CHECK and this list must move together — `activities` is returned
   * through a response schema, so a type the database accepts and this does
   * not is a 500 on the timeline rather than a validation error on the write.
   */
  "whatsapp_received",
  "whatsapp_auto_replied",
  "lead_rating_changed",
  "follow_up_created",
  "follow_up_completed",
  "follow_up_missed",
  "follow_up_rescheduled",
  "follow_up_cancelled",
  "contact_merged"
];
var Activity = Type2.Object({
  id: Type2.Integer(),
  contactId: Type2.String({ format: "uuid" }),
  leadId: Type2.Union([Type2.String({ format: "uuid" }), Type2.Null()]),
  actorName: Type2.Union([Type2.String(), Type2.Null()]),
  activityType: Type2.Union(ACTIVITY_TYPES.map((t) => Type2.Literal(t))),
  occurredAt: Type2.String({ format: "date-time" }),
  summary: Type2.String(),
  metadata: Type2.Union([Type2.Record(Type2.String(), Type2.Unknown()), Type2.Null()])
});
var ActivityPage = pageOf(Activity);
var ListActivitiesQuery = Type2.Composite([
  PageQuery,
  Type2.Object({
    contactId: Type2.String({ format: "uuid" }),
    leadId: Type2.Optional(Type2.String({ format: "uuid" }))
  })
]);

// src/auth.ts
import { Type as Type5 } from "@sinclair/typebox";

// src/timezones.ts
import { Type as Type3 } from "@sinclair/typebox";
var DEFAULT_TIMEZONE = "Asia/Kolkata";
var DEFAULT_TIMEZONE_SHORT = "IST";
var TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Qatar",
  "Asia/Riyadh",
  "Asia/Kuwait",
  "Asia/Muscat",
  "Asia/Karachi",
  "Asia/Dhaka",
  "Asia/Colombo",
  "Asia/Kathmandu",
  "Asia/Singapore",
  "Asia/Manila",
  "Europe/London",
  "America/New_York"
];
var TIMEZONE_LABELS = {
  "Asia/Kolkata": "India (IST)",
  "Asia/Dubai": "United Arab Emirates \u2014 Dubai",
  "Asia/Qatar": "Qatar \u2014 Doha",
  "Asia/Riyadh": "Saudi Arabia \u2014 Riyadh",
  "Asia/Kuwait": "Kuwait",
  "Asia/Muscat": "Oman \u2014 Muscat",
  "Asia/Karachi": "Pakistan \u2014 Karachi",
  "Asia/Dhaka": "Bangladesh \u2014 Dhaka",
  "Asia/Colombo": "Sri Lanka \u2014 Colombo",
  "Asia/Kathmandu": "Nepal \u2014 Kathmandu",
  "Asia/Singapore": "Singapore",
  "Asia/Manila": "Philippines \u2014 Manila",
  "Europe/London": "United Kingdom \u2014 London",
  "America/New_York": "United States \u2014 New York"
};
var TimezoneSchema = Type3.Union(TIMEZONES.map((tz) => Type3.Literal(tz)));
var UpdateMyTimezoneBody = Type3.Object({ timezone: TimezoneSchema });

// src/users.ts
import { Type as Type4 } from "@sinclair/typebox";
var APP_USER_ROLES = ["admin", "marketing"];
var APP_USER_ROLE_LABELS = {
  admin: "Admin",
  marketing: "Marketing"
};
var APP_USER_ROLE_DESCRIPTIONS = {
  admin: "Manages operators and targets, and sees every record in the organisation.",
  marketing: "Works leads. Sees their own records and their reportees\u2019, unless given full visibility."
};
var CrmUser = Type4.Object({
  id: Type4.String({ format: "uuid" }),
  email: Type4.String(),
  fullName: Type4.String(),
  role: enumOf(APP_USER_ROLES),
  managerId: Type4.Union([Type4.String({ format: "uuid" }), Type4.Null()]),
  /** Joined, not stored — a uuid is not something an admin can recognise. */
  managerName: Type4.Union([Type4.String(), Type4.Null()]),
  canViewAll: Type4.Boolean(),
  isActive: Type4.Boolean(),
  /** How many people report to this person directly. Shown on the tree. */
  directReportCount: Type4.Integer(),
  createdAt: Type4.String({ format: "date-time" }),
  updatedAt: Type4.String({ format: "date-time" })
});
var ListUsersQuery = Type4.Object({
  search: Type4.Optional(Type4.String({ maxLength: 120 })),
  role: Type4.Optional(enumOf(APP_USER_ROLES)),
  /**
   * Deactivated operators are hidden by default but never deleted: their name
   * still has to render on every lead and note they ever owned.
   */
  includeInactive: Type4.Optional(Type4.Boolean())
});
var CreateUserBody = Type4.Object({
  id: Type4.String({
    format: "uuid",
    description: "The Supabase Auth user id \u2014 Dashboard \u2192 Authentication \u2192 Users."
  }),
  email: Type4.String({ format: "email", maxLength: 254 }),
  fullName: Type4.String({ minLength: 1, maxLength: 200 }),
  role: Type4.Optional(enumOf(APP_USER_ROLES)),
  managerId: Type4.Optional(Type4.Union([Type4.String({ format: "uuid" }), Type4.Null()])),
  canViewAll: Type4.Optional(Type4.Boolean()),
  isActive: Type4.Optional(Type4.Boolean())
});
var UpdateUserBody = Type4.Object({
  fullName: Type4.Optional(Type4.String({ minLength: 1, maxLength: 200 })),
  role: Type4.Optional(enumOf(APP_USER_ROLES)),
  /** `null` clears the manager, making this person a root of the tree. */
  managerId: Type4.Optional(Type4.Union([Type4.String({ format: "uuid" }), Type4.Null()])),
  canViewAll: Type4.Optional(Type4.Boolean()),
  isActive: Type4.Optional(Type4.Boolean())
});

// src/auth.ts
var MeResponse = Type5.Object({
  id: Type5.String({ format: "uuid" }),
  email: Type5.String({ format: "email" }),
  fullName: Type5.String(),
  isActive: Type5.Boolean(),
  /** The tenant's own name — what `{Brand}` renders as in a template. */
  orgName: Type5.String(),
  /**
   * What this operator may do and see. The server enforces both — these are
   * here so the UI can stop offering an action it would refuse, which is the
   * difference between a disabled button and a 403 after a form is filled in.
   */
  role: enumOf(APP_USER_ROLES),
  /** Bypasses the manager-subtree narrowing. Admins always see everything. */
  canViewAll: Type5.Boolean(),
  managerId: Type5.Union([Type5.String({ format: "uuid" }), Type5.Null()]),
  /**
   * The clock this operator's screens run on — see `timezones.ts`.
   *
   * On `me` rather than fetched separately because every screen that renders a
   * time needs it, and a second round trip would leave the first paint showing
   * Indian hours to somebody in Dubai and then moving them.
   */
  timezone: TimezoneSchema,
  createdAt: Type5.String({ format: "date-time" })
});
var LoginRequest = Type5.Object({
  email: Type5.String({ format: "email", maxLength: 254 }),
  password: Type5.String({ minLength: 8, maxLength: 128 })
});

// src/briefing.ts
import { Type as Type8 } from "@sinclair/typebox";

// src/call-analysis.ts
import { Type as Type7 } from "@sinclair/typebox";

// src/leads.ts
import { Type as Type6 } from "@sinclair/typebox";
var LEAD_STATUSES = [
  "new",
  "not_contacted",
  "contacted",
  "interested",
  /*
   * THE DEMO, as two statuses rather than four unrelated fragments.
   *
   * The owner describes the lifecycle as lead -> follow-ups -> DEMO ->
   * converted or lost, and the demo was the one step not modelled anywhere:
   * it existed as a follow-up type, a call intent, a seeded tag, and a DEAL
   * stage called "Demo Given" that was unreachable because converting a lead
   * is the sale. Booking a demo changed nothing on the lead, completing one
   * recorded a generic "follow-up completed", and a no-show was
   * indistinguishable from a demo that went well.
   *
   * There is a comment elsewhere in this codebase arguing a demo is an event
   * and not a status. That is right in the abstract and wrong for this
   * business: the demo is the only step on this funnel with a yes/no outcome,
   * which makes it a change in what the lead IS. "Demos booked versus held
   * versus converted" is one query now instead of none.
   */
  "demo_scheduled",
  "demo_done",
  "not_interested",
  "unqualified",
  "converted",
  "lost"
];
var LEAD_STATUSES_CLOSED = ["converted", "lost", "not_interested", "unqualified"];
var LEAD_STATUSES_NEEDING_REASON = ["lost", "not_interested", "unqualified"];
var LEAD_LOST_REASONS = [
  "price",
  "competitor",
  "no_reply",
  "not_a_fit",
  "too_early",
  "has_supplier",
  "test_record",
  "other"
];
var LEAD_LOST_REASON_LABELS = {
  price: "Too expensive",
  competitor: "Went with a competitor",
  no_reply: "Never replied",
  not_a_fit: "Not a fit for us",
  too_early: "Too early \u2014 no shop or stock yet",
  has_supplier: "Already has a supplier",
  /** Ours, not theirs. Keeps probes and duplicates out of the loss numbers. */
  test_record: "Test or duplicate record",
  other: "Something else"
};
var LEAD_RATINGS = ["hot", "warm", "cold"];
var LEAD_TYPES = ["new_business", "renewal", "upsell", "reactivation"];
var LEAD_SOURCES = ["frejun", "whatsapp", "instagram", "facebook", "website_help", "manual"];
var LEAD_STATUS_LABELS = {
  new: "New",
  not_contacted: "Not contacted",
  contacted: "Contacted",
  interested: "Interested",
  demo_scheduled: "Demo booked",
  demo_done: "Demo done",
  not_interested: "Not interested",
  unqualified: "Unqualified",
  converted: "Converted",
  lost: "Lost"
};
var LEAD_RATING_LABELS = {
  hot: "Hot",
  warm: "Warm",
  cold: "Cold"
};
var LEAD_TYPE_LABELS = {
  new_business: "New business",
  renewal: "Renewal",
  upsell: "Upsell",
  reactivation: "Reactivation"
};
var LEAD_SOURCE_LABELS = {
  frejun: "FreJun call",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Facebook",
  website_help: "Need help? from our website",
  manual: "Manual entry"
};
var isLeadClosed = (status) => LEAD_STATUSES_CLOSED.includes(status);
var leadStatusNeedsReason = (status) => LEAD_STATUSES_NEEDING_REASON.includes(status);
var LeadContactSummary = Type6.Object({
  id: Type6.String({ format: "uuid" }),
  /** Null when the customer has no number; the Call button hides itself. */
  phoneE164: Type6.Union([PhoneE164, Type6.Null()]),
  /** Carried on the lead so "try their other number" needs no second request. */
  altPhoneE164: Type6.Union([PhoneE164, Type6.Null()]),
  fullName: Type6.Union([Type6.String(), Type6.Null()]),
  doNotContact: Type6.Boolean()
});
var LEAD_NOTE_PREVIEW = 3;
var LEAD_NOTE_PREVIEW_CHARS = 280;
var LeadNotePreview = Type6.Object({
  /** The note's own id. A timestamp is not an identity: two notes written in
   *  one transaction share one, and a list keyed on that drops the second. */
  id: Type6.Integer(),
  body: Type6.String(),
  /** The body was cut at `LEAD_NOTE_PREVIEW_CHARS`; the rest is on the lead. */
  truncated: Type6.Boolean(),
  createdAt: Type6.String({ format: "date-time" }),
  authorName: Type6.Union([Type6.String(), Type6.Null()])
});
var Lead = Type6.Object({
  id: Type6.String({ format: "uuid" }),
  contactId: Type6.String({ format: "uuid" }),
  contact: LeadContactSummary,
  name: Type6.String(),
  leadStatus: enumOf(LEAD_STATUSES),
  leadRating: Type6.Union([enumOf(LEAD_RATINGS), Type6.Null()]),
  leadType: Type6.Union([enumOf(LEAD_TYPES), Type6.Null()]),
  leadSource: enumOf(LEAD_SOURCES),
  campaignId: Type6.Union([Type6.String({ format: "uuid" }), Type6.Null()]),
  campaignName: Type6.Union([Type6.String(), Type6.Null()]),
  productInterest: Type6.Union([Type6.String(), Type6.Null()]),
  /**
   * Which of the customer's businesses this opportunity is for. One seller
   * running a pet store and a grocery is two leads, and without this they
   * read as duplicates in every list.
   */
  businessName: Type6.Union([Type6.String(), Type6.Null()]),
  businessType: Type6.Union([Type6.String(), Type6.Null()]),
  dealId: Type6.Union([Type6.String({ format: "uuid" }), Type6.Null()]),
  convertedAt: Type6.Union([Type6.String({ format: "date-time" }), Type6.Null()]),
  lostReason: Type6.Union([Type6.String(), Type6.Null()]),
  /**
   * The countable half of the reason. Null on every lead closed before this
   * existed, and on anything closed through the API without one — which is
   * why a report on it has to show "Not categorised" rather than assume.
   */
  lostReasonCode: Type6.Union([enumOf(LEAD_LOST_REASONS), Type6.Null()]),
  ownerId: Type6.Union([Type6.String({ format: "uuid" }), Type6.Null()]),
  ownerName: Type6.Union([Type6.String(), Type6.Null()]),
  /**
   * §3's vocabulary, carried on the row rather than fetched per lead.
   * "Hot Lead + Pricing Required" is how §12 says an operator scans a list,
   * and a tag only reachable by opening the lead cannot serve that.
   */
  tags: Type6.Array(
    Type6.Object({
      id: Type6.String({ format: "uuid" }),
      name: Type6.String(),
      color: Type6.Union([Type6.String(), Type6.Null()])
    })
  ),
  /**
   * The newest notes on this lead, for the list's hover panel. Newest first.
   *
   * Carried on the row for the same reason the tags are: an operator scanning
   * a list needs to know what was last said without opening every lead. It is
   * a PREVIEW, capped at `LEAD_NOTE_PREVIEW` — the full history is the lead's
   * own page, and `noteCount` says whether there is more.
   */
  recentNotes: Type6.Array(LeadNotePreview),
  /** Every note on this lead, not just the previewed ones. */
  noteCount: Type6.Integer(),
  /**
   * The soonest still-pending follow-up on this lead, or null.
   *
   * Enough to SAY what is booked, deliberately not a whole `FollowUp`: that
   * shape carries `dueOn`, `dueMinutes` and `isOverdue`, all derived with
   * `at time zone` in the follow-ups module. Re-deriving them inside the
   * leads query is how two screens start disagreeing about which day a
   * 02:00 IST follow-up falls on. Anything needing the full row loads it.
   */
  nextFollowUp: Type6.Union([
    Type6.Object({
      dueAt: Type6.String({ format: "date-time" }),
      followUpType: enumOf(FOLLOW_UP_TYPES)
    }),
    Type6.Null()
  ]),
  createdAt: Type6.String({ format: "date-time" }),
  updatedAt: Type6.String({ format: "date-time" })
});
var CreateLeadResponse = Type6.Object({
  lead: Lead,
  merged: Type6.Boolean()
});
var LeadPage = pageOf(Lead);
var LEAD_SORTS = [
  "created_desc",
  "created_asc",
  "updated_desc",
  "rating_desc",
  "status_asc",
  "name_asc"
];
var ListLeadsQuery = Type6.Composite([
  PageQuery,
  Type6.Object({
    search: Type6.Optional(Type6.String({ maxLength: 120 })),
    leadStatus: Type6.Optional(enumOf(LEAD_STATUSES)),
    leadRating: Type6.Optional(enumOf(LEAD_RATINGS)),
    leadType: Type6.Optional(enumOf(LEAD_TYPES)),
    leadSource: Type6.Optional(enumOf(LEAD_SOURCES)),
    campaignId: Type6.Optional(Type6.String({ format: "uuid" })),
    ownerId: Type6.Optional(Type6.String({ format: "uuid" })),
    contactId: Type6.Optional(Type6.String({ format: "uuid" })),
    /** True shows only open leads, which is the default working view. */
    openOnly: Type6.Optional(Type6.Boolean()),
    /**
     * Only leads with a call the analyser flagged for a human to hear.
     *
     * Cleared the same way the Needs Attention queue clears one — by a
     * manager stamping `reviewed_at` — so this filter and that queue cannot
     * come to disagree about what is still outstanding.
     */
    needsAttention: Type6.Optional(Type6.Boolean()),
    createdFrom: Type6.Optional(DateOnly),
    createdTo: Type6.Optional(DateOnly),
    /**
     * Converted inside a window. Distinct from createdFrom/To because a lead
     * opened in August and converted in September belongs to September's
     * number — which is what the team page counts, so the list it links to
     * has to agree or the click contradicts the figure it came from.
     */
    convertedFrom: Type6.Optional(DateOnly),
    convertedTo: Type6.Optional(DateOnly),
    /**
     * "Worked" in a window: opened in it, or touched by any timeline entry
     * inside it. Mirrors the team page's leads-worked column exactly.
     */
    workedFrom: Type6.Optional(DateOnly),
    workedTo: Type6.Optional(DateOnly),
    sort: Type6.Optional(enumOf(LEAD_SORTS))
  })
]);
var CreateLeadBody = Type6.Object({
  contactId: Type6.Optional(Type6.String({ format: "uuid" })),
  /**
   * Which open lead this entry belongs to, when the customer has more than
   * one and the server has asked.
   *
   * The second half of the `open_lead_exists` conversation: refuse with the
   * candidates, the operator picks, the same request comes back naming one.
   * Ignored when the customer has no open lead or only one — a single open
   * lead is merged into without asking, because there is nothing to ask.
   */
  mergeIntoLeadId: Type6.Optional(Type6.String({ format: "uuid" })),
  /**
   * "None of these — it really is a new enquiry."
   *
   * The other answer to the same question, and it has to exist: a customer
   * who already has two open enquiries may genuinely be opening a third, and
   * a refusal with no way past it would leave the operator stuck on a form
   * they cannot submit. Only consulted when the server would otherwise ask.
   */
  forceNewLead: Type6.Optional(Type6.Boolean()),
  newContact: Type6.Optional(
    Type6.Object({
      /** A number, a name, or both. The API refuses neither. */
      phoneE164: Type6.Optional(PhoneE164Input),
      /** Optional second number, captured while the operator has them on the line. */
      altPhoneE164: Type6.Optional(PhoneE164Input),
      /**
       * Optional, like `CreateContactBody.fullName` and like the column.
       * A call can land before anyone learns the name, and refusing the lead
       * until someone types one loses the lead rather than the name.
       */
      fullName: Type6.Optional(Type6.String({ minLength: 1, maxLength: 200 }))
    })
  ),
  /**
   * OPTIONAL, and derived when it is left out.
   *
   * It was required, which made it the single most-typed useless field in
   * the day: the form already computed "<customer> – <product>" and offered
   * it as a placeholder, and the submit line `clean.name || suggestedTitle`
   * could never run because validation had already refused a blank. So an
   * agent typed, fifty times a day, the exact string the machine had just
   * worked out and shown them.
   *
   * The server derives it the same way the two ingest paths already name
   * their leads, so a typed lead and an ingested one read alike in the list.
   */
  name: Type6.Optional(Type6.String({ minLength: 1, maxLength: 200 })),
  leadSource: enumOf(LEAD_SOURCES),
  leadStatus: Type6.Optional(enumOf(LEAD_STATUSES)),
  leadRating: Type6.Optional(enumOf(LEAD_RATINGS)),
  leadType: Type6.Optional(enumOf(LEAD_TYPES)),
  campaignId: Type6.Optional(Type6.String({ format: "uuid" })),
  productInterest: Type6.Optional(Type6.String({ maxLength: 500 })),
  businessName: Type6.Optional(Type6.String({ maxLength: 200 })),
  businessType: Type6.Optional(Type6.String({ maxLength: 120 })),
  ownerId: Type6.Optional(Type6.String({ format: "uuid" })),
  /** Writes a note row plus a timeline entry, not a column on the lead. */
  note: Type6.Optional(Type6.String({ maxLength: 5e3 })),
  /** Optional inline sub-form, created in the same transaction. */
  followUp: Type6.Optional(
    Type6.Object({
      dueAt: Type6.String({ format: "date-time" }),
      // From the shared list, not a second copy of it — an inline literal
      // here is how 'demo' became valid in the database and a 400 at the API.
      followUpType: enumOf(FOLLOW_UP_TYPES),
      priority: Type6.Optional(enumOf(FOLLOW_UP_PRIORITIES)),
      remindAt: Type6.Optional(Type6.String({ format: "date-time" })),
      note: Type6.Optional(Type6.String({ maxLength: 2e3 }))
    })
  )
});
var SUPPORT_NOTE_MAX = 500;
var UpdateLeadBody = Type6.Partial(
  Type6.Object({
    name: Type6.String({ minLength: 1, maxLength: 200 }),
    leadRating: Type6.Union([enumOf(LEAD_RATINGS), Type6.Null()]),
    leadType: Type6.Union([enumOf(LEAD_TYPES), Type6.Null()]),
    leadSource: enumOf(LEAD_SOURCES),
    campaignId: Type6.Union([Type6.String({ format: "uuid" }), Type6.Null()]),
    productInterest: Type6.Union([Type6.String({ maxLength: 500 }), Type6.Null()]),
    businessName: Type6.Union([Type6.String({ maxLength: 200 }), Type6.Null()]),
    businessType: Type6.Union([Type6.String({ maxLength: 120 }), Type6.Null()]),
    ownerId: Type6.Union([Type6.String({ format: "uuid" }), Type6.Null()]),
    /**
     * The arrivals sheet's own cell — see `leads.support_note`.
     *
     * Edited through the ordinary lead PATCH rather than its own endpoint:
     * it is a field on the lead with no precondition and no timeline entry,
     * which is exactly what this route is for.
     */
    supportNote: Type6.Union([Type6.String({ maxLength: SUPPORT_NOTE_MAX }), Type6.Null()])
  })
);
var LEAD_STATUSES_SETTABLE = [
  "new",
  "not_contacted",
  "contacted",
  "interested",
  // Settable by hand as well as reached automatically: a demo arranged over
  // the phone without a follow-up being booked still happened.
  "demo_scheduled",
  "demo_done",
  "not_interested",
  "unqualified",
  "lost"
];
var ChangeLeadStatusBody = Type6.Object({
  leadStatus: enumOf(LEAD_STATUSES_SETTABLE),
  /** Required by the database CHECK for lost / not_interested / unqualified. */
  reason: Type6.Optional(Type6.String({ minLength: 1, maxLength: 500 })),
  /**
   * Optional here and required by the UI. Optional because the sweeps and
   * every existing caller close leads without one, and rejecting those would
   * turn a reporting improvement into an outage; required on the form because
   * the one place a human is closing a lead is the only place the honest
   * answer is known. See LEAD_LOST_REASONS.
   */
  reasonCode: Type6.Optional(enumOf(LEAD_LOST_REASONS))
});
var ConvertLeadBody = Type6.Object({
  title: Type6.String({ minLength: 1, maxLength: 200 }),
  value: Type6.Optional(Type6.Number({ minimum: 0 })),
  expectedCloseOn: Type6.Optional(DateOnly),
  stageId: Type6.Optional(Type6.String({ format: "uuid" }))
});
var OpenLeadCandidate = Type6.Object({
  id: Type6.String({ format: "uuid" }),
  name: Type6.String(),
  leadStatus: enumOf(LEAD_STATUSES),
  /** Newest first, so "the one they are talking about" is usually the top. */
  updatedAt: Type6.String({ format: "date-time" })
});
var OpenLeadConflictResponse = Type6.Object({
  error: Type6.Literal("open_lead_exists"),
  message: Type6.String(),
  existingLeadId: Type6.String({ format: "uuid" }),
  candidates: Type6.Array(OpenLeadCandidate)
});
var BULK_CLOSE_MAX = 100;
var BulkCloseLeadsBody = Type6.Object({
  leadIds: Type6.Array(Type6.String({ format: "uuid" }), { minItems: 1, maxItems: BULK_CLOSE_MAX }),
  /** Only the closed statuses; `converted` is a deal, not a bulk gesture. */
  leadStatus: enumOf(["lost", "not_interested", "unqualified"]),
  /** Required by the same rule, and bounded the same way, as a single close. */
  lostReason: Type6.String({ minLength: 1, maxLength: 500 }),
  /** One code for the whole batch, like the sentence. See LEAD_LOST_REASONS. */
  lostReasonCode: Type6.Optional(enumOf(LEAD_LOST_REASONS))
});
var BulkCloseLeadsResponse = Type6.Object({
  /** How many actually moved. Fewer than asked means some were already closed. */
  closed: Type6.Integer(),
  skipped: Type6.Integer()
});

// src/call-analysis.ts
var CALL_ANALYSIS_STATUSES = ["pending", "running", "done", "failed", "skipped"];
var CALL_ANALYSIS_STATUS_LABELS = {
  pending: "Queued",
  running: "Analysing",
  done: "Analysed",
  failed: "Failed",
  // The operator's question is "why is there no summary", and "Nothing to
  // analyse" answers it where "Skipped" just restates the status.
  skipped: "Nothing to analyse"
};
var CALL_DEMEANOURS = ["friendly", "businesslike", "rushed", "reserved", "frustrated"];
var CALL_DEMEANOUR_LABELS = {
  friendly: "Friendly",
  businesslike: "Businesslike",
  rushed: "In a hurry",
  reserved: "Quiet",
  frustrated: "Frustrated"
};
var CALL_DEMEANOUR_HINTS = {
  friendly: "Warm opener works. They will give you time.",
  businesslike: "Get to the point. Lead with the answer.",
  rushed: "Keep it short and book a proper slot.",
  reserved: "Ask more questions \u2014 they will not volunteer much.",
  frustrated: "Acknowledge the problem before anything else."
};
var CALL_INTENTS = [
  "pricing",
  "demo",
  "onboarding",
  "support",
  "complaint",
  "cancellation",
  "renewal",
  "chasing_update",
  "other"
];
var CALL_INTENT_LABELS = {
  pricing: "Pricing",
  demo: "Demo",
  onboarding: "Onboarding",
  support: "Support",
  complaint: "Complaint",
  cancellation: "Cancellation",
  renewal: "Renewal",
  chasing_update: "Chasing an update",
  other: "Other"
};
var CALL_REVIEW_REASONS = [
  "customer_angry",
  "agent_gave_wrong_information",
  "agent_overpromised",
  "unresolved_complaint",
  "cancellation_risk",
  "pricing_dispute"
];
var CALL_REVIEW_REASON_LABELS = {
  customer_angry: "Customer was angry",
  agent_gave_wrong_information: "Wrong information given",
  agent_overpromised: "Promised more than we offer",
  unresolved_complaint: "Complaint left unresolved",
  cancellation_risk: "Talked about leaving",
  pricing_dispute: "Disputed what they were charged"
};
var CALL_REVIEW_REASON_HINTS = {
  customer_angry: "Listen before anyone rings them back.",
  agent_gave_wrong_information: "Check the recording and correct it with the customer.",
  agent_overpromised: "Confirm what was promised can actually be delivered.",
  unresolved_complaint: "Nobody closed this off on the call.",
  cancellation_risk: "They raised leaving. Worth a call from you.",
  pricing_dispute: "Check the account before this escalates."
};
var CALL_RISK_LEVELS = ["none", "low", "medium", "high"];
var CALL_RISK_LEVEL_LABELS = {
  none: "No risk signs",
  low: "Low risk",
  medium: "Medium risk",
  high: "High risk"
};
var CALL_SENTIMENTS = ["positive", "neutral", "negative", "mixed"];
var CALL_SENTIMENT_LABELS = {
  positive: "Positive",
  neutral: "Neutral",
  negative: "Negative",
  mixed: "Mixed"
};
var LEAD_PRIORITIES = ["P1_urgent", "P2_high", "P3_medium", "P4_low"];
var LEAD_PRIORITY_LABELS = {
  P1_urgent: "Urgent",
  P2_high: "High",
  P3_medium: "Medium",
  P4_low: "Low"
};
var LEAD_QUALITIES = ["high", "medium", "low", "junk"];
var LEAD_QUALITY_LABELS = {
  high: "Good fit",
  medium: "Possible fit",
  low: "Weak fit",
  junk: "Not a lead"
};
var AiCoachScript = Type7.Object({
  /** What the customer said that created the moment. */
  context: Type7.String(),
  /** What the agent actually replied. */
  agentSaid: Type7.String(),
  /** The habit behind it, so the lesson outlives this one call. */
  learningObjective: Type7.String(),
  /** Words they could actually use next time. */
  recommendedScript: Type7.String()
});
var ConversationIntelligence = Type7.Object({
  keyTopics: Type7.Array(Type7.String()),
  customerQuestions: Type7.Array(Type7.String()),
  engagementLevel: Type7.Union([Type7.String(), Type7.Null()]),
  objectionSeverity: Type7.Union([Type7.String(), Type7.Null()]),
  sentimentTrajectory: Type7.Union([Type7.String(), Type7.Null()])
});
var NextBestActionDetails = Type7.Object({
  channel: Type7.Union([Type7.String(), Type7.Null()]),
  suggestedTime: Type7.Union([Type7.String(), Type7.Null()]),
  recommendedPlan: Type7.Union([Type7.String(), Type7.Null()]),
  strategicRationale: Type7.Union([Type7.String(), Type7.Null()])
});
var CallAnalysis = Type7.Object({
  callId: Type7.Integer(),
  status: enumOf(CALL_ANALYSIS_STATUSES),
  /** Which model produced it. Prompts and models change; conclusions do not travel. */
  model: Type7.Union([Type7.String(), Type7.Null()]),
  summary: Type7.Union([Type7.String(), Type7.Null()]),
  sentiment: Type7.Union([enumOf(CALL_SENTIMENTS), Type7.Null()]),
  /**
   * One sentence of why. A label nobody can argue with is a label nobody
   * should trust — the first time it calls a happy customer negative,
   * somebody has to be able to see what it heard.
   */
  sentimentNote: Type7.Union([Type7.String(), Type7.Null()]),
  /**
   * How likely this customer is to buy, from the call.
   *
   * `hot | warm | cold`, the SAME vocabulary as `LEAD_RATINGS` and the
   * WhatsApp reply scorer. A fourth scale would make "hot on WhatsApp, 4/5 on
   * the call" a sentence nobody can act on.
   *
   * A SEPARATE AXIS FROM SENTIMENT, which is the whole reason it exists. A
   * seller who is angry about delivery delays because they are about to scale
   * is negative and hot; a delightful call ending in "I'll think about it" is
   * positive and cold. Read one off the other and you chase the wrong half of
   * the pipeline.
   *
   * It is NEVER written to `leads.lead_rating`. It is shown beside it and a
   * human applies it — unlike the WhatsApp scorer, which may write because it
   * is a pure keyword function somebody can read and predict. This is a black
   * box whose transcription of Tamil is still unproven.
   */
  potential: Type7.Union([enumOf(LEAD_RATINGS), Type7.Null()]),
  potentialNote: Type7.Union([Type7.String(), Type7.Null()]),
  /** How they were to talk to, and one line of what was observed. */
  demeanour: Type7.Union([enumOf(CALL_DEMEANOURS), Type7.Null()]),
  demeanourNote: Type7.Union([Type7.String(), Type7.Null()]),
  /**
   * What they said about the PRODUCT itself — praise, complaints, a feature
   * they liked or could not find.
   *
   * Separate from `blocker`, which is what stops them buying. "The dashboard
   * is slow" is a product view; "we have no budget until April" is a blocker,
   * and lumping them together loses the half that product should hear.
   *
   * Free text, and NOT written into `lead_requirements`. That is the step
   * docs/08 §5 keeps until last, because a hallucinated "integration" on
   * three calls becomes a roadmap item nobody asked for.
   */
  productView: Type7.Union([Type7.String(), Type7.Null()]),
  /** The feature, price or capability they actually asked about. */
  askedFor: Type7.Union([Type7.String(), Type7.Null()]),
  /** What is stopping them, in their words. The line nobody writes down. */
  blocker: Type7.Union([Type7.String(), Type7.Null()]),
  /** What the agent committed to on the call. The thing that gets forgotten. */
  promised: Type7.Union([Type7.String(), Type7.Null()]),
  /**
   * Did the call end with a concrete next step?
   *
   * The single most predictive thing about whether a deal moves, and it is a
   * yes/no rather than a judgement — "I'll call you Tuesday" counts, "we'll
   * be in touch" does not.
   */
  nextStepAgreed: Type7.Union([Type7.Boolean(), Type7.Null()]),
  /**
   * Share of the conversation the AGENT spoke, 0–100.
   *
   * Counted off the transcript rather than guessed, so it is the one number
   * here that is close to a measurement. A rep at 80% is talking at the
   * customer, and that is coachable in a way "the call went badly" is not.
   */
  talkRatioAgentPercent: Type7.Union([Type7.Integer(), Type7.Null()]),
  /* ------------------------------------------------------------------------
   * Why the call happened, and what it says about the market.
   * --------------------------------------------------------------------- */
  /** One word, so a quarter of calls can be counted rather than read. */
  intent: Type7.Union([enumOf(CALL_INTENTS), Type7.Null()]),
  rootCause: Type7.Union([Type7.String(), Type7.Null()]),
  /**
   * Who they named as an alternative, in their words. Free text rather than
   * a list: the competitor nobody has heard of yet is the one worth knowing.
   */
  competitorMentioned: Type7.Union([Type7.String(), Type7.Null()]),
  riskLevel: Type7.Union([enumOf(CALL_RISK_LEVELS), Type7.Null()]),
  riskNote: Type7.Union([Type7.String(), Type7.Null()]),
  /** One thing to do next. A suggestion to a human, never an action. */
  nextBestAction: Type7.Union([Type7.String(), Type7.Null()]),
  /** What the agent could have done better on THIS call. */
  coachingNote: Type7.Union([Type7.String(), Type7.Null()]),
  /* ------------------------------------------------------------------------
   * How the call was handled, shown to the agent it is about.
   *
   * This block reverses what this file used to say — that a number against a
   * named colleague is a performance review nobody agreed to. The owner asked
   * for the score and asked for the agent to see their own, so the honest
   * design is to make it legible rather than to half-hide it: the score never
   * ships without the two pieces of text that explain it, and nothing in the
   * API ranks agents against each other.
   * --------------------------------------------------------------------- */
  /** 0-100 for one call. One model's read of one recording, not a rating. */
  callScore: Type7.Union([Type7.Integer(), Type7.Null()]),
  /** What was explained well and what to do differently next time. */
  agentPitchFeedback: Type7.Union([Type7.String(), Type7.Null()]),
  /**
   * THE SCORECARD — 0-10 for each part of a call.
   *
   * A whole-call score says a call was a 72 and leaves nobody knowing what to
   * practise. These say which part was weak, which is the difference between
   * a grade and a lesson.
   */
  scoreGreeting: Type7.Union([Type7.Integer(), Type7.Null()]),
  scoreDiscovery: Type7.Union([Type7.Integer(), Type7.Null()]),
  scorePitch: Type7.Union([Type7.Integer(), Type7.Null()]),
  scoreObjection: Type7.Union([Type7.Integer(), Type7.Null()]),
  scoreClosing: Type7.Union([Type7.Integer(), Type7.Null()]),
  /**
   * The same scores WITH the sentence justifying each, keyed by dimension.
   *
   * `Record` rather than a fixed object: the five above are the stable subset
   * a report can count on, and this is whatever the model chose to break the
   * call into. A UI that hard-coded five keys would silently drop a sixth.
   */
  scoreBreakdown: Type7.Union([
    Type7.Record(Type7.String(), Type7.Object({ score: Type7.Integer(), notes: Type7.String() })),
    Type7.Null()
  ]),
  /** The call in four or five timestamped lines — the scannable version. */
  callChapters: Type7.Union([Type7.Array(Type7.Object({ time: Type7.String(), topic: Type7.String() })), Type7.Null()]),
  /** What the customer did that suggests they will buy. A checklist, not a paragraph. */
  buyingSignals: Type7.Union([Type7.Array(Type7.String()), Type7.Null()]),
  /** The one thing holding them back, where `blocker` is anything in the way. */
  primaryObjection: Type7.Union([Type7.String(), Type7.Null()]),
  /** Who on the other end can actually say yes. */
  decisionMaker: Type7.Union([Type7.String(), Type7.Null()]),
  /** Something they wanted that the product does not do — a roadmap input. */
  productGapFeedback: Type7.Union([Type7.String(), Type7.Null()]),
  /* ------------------------------------------------------------------------
   * Prioritisation — what to do about this call, as opposed to how it went.
   * --------------------------------------------------------------------- */
  /** 0-100, how worth chasing this looks. Ranks; does not forecast. */
  predictiveScore: Type7.Union([Type7.Integer(), Type7.Null()]),
  leadPriority: Type7.Union([enumOf(LEAD_PRIORITIES), Type7.Null()]),
  /** 0-100. Separate from the score on purpose: one ranks, one forecasts. */
  conversionProbability: Type7.Union([Type7.Integer(), Type7.Null()]),
  /** Why it landed in that bucket, in a sentence somebody can argue with. */
  priorityReason: Type7.Union([Type7.String(), Type7.Null()]),
  /** Null once they have converted — the question stops meaning anything. */
  leadQuality: Type7.Union([enumOf(LEAD_QUALITIES), Type7.Null()]),
  leadQualityReason: Type7.Union([Type7.String(), Type7.Null()]),
  /** The coaching message, ready for a manager to send. Praise first. */
  coachingPingSummary: Type7.Union([Type7.String(), Type7.Null()]),
  /** The one habit behind the weak scores. */
  skillGap: Type7.Union([Type7.String(), Type7.Null()]),
  /** What they should have said, at the moments it mattered. */
  aiCoachScripts: Type7.Union([Type7.Array(AiCoachScript), Type7.Null()]),
  conversationIntelligence: Type7.Union([ConversationIntelligence, Type7.Null()]),
  nextBestActionDetails: Type7.Union([NextBestActionDetails, Type7.Null()]),
  /**
   * WHAT THE CUSTOMER COMMITTED TO, as opposed to what we did.
   *
   * `promised` is our side of the call. This is theirs, and it is the half
   * nothing in this CRM recorded — "he will send the product list tomorrow"
   * is a thing to chase, with a date to chase it on.
   */
  customerCommitment: Type7.Union([Type7.String(), Type7.Null()]),
  expectedClientDetails: Type7.Union([Type7.String(), Type7.Null()]),
  clientCommitmentDueAt: Type7.Union([Type7.String({ format: "date-time" }), Type7.Null()]),
  /**
   * The follow-up message, written and ready to send.
   *
   * A DRAFT AND NOT A SEND. Nothing here messages a customer without a human
   * pressing something: the UI copies it or opens WhatsApp with it prefilled,
   * and the agent is the one who presses send.
   */
  whatsappFollowupDraft: Type7.Union([Type7.String(), Type7.Null()]),
  /* ------------------------------------------------------------------------
   * The manager's queue, and what the analysis actually DID.
   * --------------------------------------------------------------------- */
  /** Null on the overwhelming majority of calls, which is the point. */
  reviewReason: Type7.Union([enumOf(CALL_REVIEW_REASONS), Type7.Null()]),
  reviewNote: Type7.Union([Type7.String(), Type7.Null()]),
  reviewedAt: Type7.Union([Type7.String(), Type7.Null()]),
  /**
   * The broad "somebody senior should look at this".
   *
   * Feeds the one Needs Attention queue rather than a screen of its own —
   * see the column comment. Always present, never null: an unflagged call is
   * an answer, and the common one.
   */
  needsAttention: Type7.Boolean(),
  /**
   * The callback the agent promised, resolved to an instant by the model.
   *
   * `createdFollowUpId` is how the UI knows whether this was BOOKED or is
   * merely being suggested — above the confidence threshold it is already in
   * somebody's list, below it there is a button.
   */
  followUpAt: Type7.Union([Type7.String(), Type7.Null()]),
  followUpType: Type7.Union([enumOf(FOLLOW_UP_TYPES), Type7.Null()]),
  followUpReason: Type7.Union([Type7.String(), Type7.Null()]),
  followUpConfidence: Type7.Union([Type7.Integer(), Type7.Null()]),
  createdFollowUpId: Type7.Union([Type7.String(), Type7.Null()]),
  /** Likewise: was the rating applied, or is it still a suggestion? */
  potentialConfidence: Type7.Union([Type7.Integer(), Type7.Null()]),
  appliedRating: Type7.Boolean(),
  /** Null in a list response; the full text on a single-call read. */
  transcript: Type7.Optional(Type7.Union([Type7.String(), Type7.Null()])),
  /** Why it gave up, in the operator's words. A queue that cannot say is one nobody trusts. */
  failureReason: Type7.Union([Type7.String(), Type7.Null()]),
  attempts: Type7.Integer(),
  /** The bill, attributable to a call rather than to one line on an invoice. */
  audioSeconds: Type7.Union([Type7.Integer(), Type7.Null()]),
  analysedAt: Type7.Union([Type7.String({ format: "date-time" }), Type7.Null()])
});
var AnalyseCallBody = Type7.Object({
  force: Type7.Optional(Type7.Boolean({ default: false }))
});
var ANALYSE_CALL_RESULTS = [
  "queued",
  "already_analysed",
  "no_recording",
  "too_short",
  "not_connected",
  "disabled"
];
var ANALYSE_CALL_RESULT_MESSAGES = {
  queued: "Queued for analysis. The summary appears on the timeline when it is ready.",
  already_analysed: "This call has already been analysed.",
  no_recording: "FreJun has no recording for this call, so there is nothing to transcribe.",
  too_short: "Too short to hold a conversation worth summarising.",
  not_connected: "Nobody answered this call, so there is no conversation on it.",
  disabled: "Call analysis is switched off. An admin can enable it in Settings \u2192 Calling."
};
var AnalyseCallResponse = Type7.Object({
  result: enumOf(ANALYSE_CALL_RESULTS),
  /** Shown to the operator as-is. */
  message: Type7.String(),
  analysis: Type7.Union([CallAnalysis, Type7.Null()])
});
var GeminiSettings = Type7.Object({
  isEnabled: Type7.Boolean(),
  hasApiKey: Type7.Boolean(),
  model: Type7.String(),
  /** Minutes of audio per org per day. 0 means no analysis, not unlimited. */
  dailyAudioMinuteCap: Type7.Integer(),
  /** Used today, so the number in the UI is the one the cap is checked against. */
  audioMinutesToday: Type7.Integer(),
  minimumDurationSeconds: Type7.Integer(),
  /** At or above this confidence, the analysis acts instead of suggesting. */
  autoActionConfidence: Type7.Integer(),
  /**
   * The SECOND switch, for WhatsApp conversations.
   *
   * Separate from `isEnabled` on purpose. An admin who consented to sending
   * call recordings to Google has not consented to sending every
   * conversation the company has ever had, and a chat export can span years
   * where a call is ninety seconds.
   */
  chatAnalysisEnabled: Type7.Boolean(),
  /** Conversations per org per day. A count: there is no audio to measure. */
  dailyChatCap: Type7.Integer(),
  chatsToday: Type7.Integer(),
  /**
   * Whether the sweeps go LOOKING for work, as opposed to processing what
   * somebody clicked.
   *
   * BOTH DEFAULT OFF, and this is the field that matters most on this page.
   * The free tier allows twenty requests per day per model. Auto-discovery
   * queues every connected call over the minimum duration that has a
   * recording, which on nineteen connected calls a day exhausts the whole
   * day's quota before anybody presses anything — and the owner's
   * understanding was that the integration was click-triggered precisely to
   * avoid that.
   *
   * Switching these off does NOT stop the sweeps: they are also the workers
   * that process a click, and a queue with no worker is a button that
   * silently does nothing.
   */
  autoAnalyseCalls: Type7.Boolean(),
  autoAnalyseChats: Type7.Boolean(),
  /**
   * Which model reads a CONVERSATION. Calls are not configurable — they are
   * audio, and only a multimodal model can read them.
   */
  chatAnalysisProvider: Type7.String(),
  /** Write-only, like every other credential here. */
  hasClaudeKey: Type7.Boolean(),
  claudeModel: Type7.String()
});
var GEMINI_MODEL_DEFAULT = "gemini-3.8-flash";
var GEMINI_DAILY_MINUTE_CAP_DEFAULT = 300;
var GEMINI_AUTO_ACTION_CONFIDENCE_DEFAULT = 80;
var GEMINI_MIN_DURATION_SECONDS_DEFAULT = 30;
var UpdateGeminiSettingsBody = Type7.Object({
  isEnabled: Type7.Optional(Type7.Boolean()),
  /** Write-only. An empty string clears it, which also forces `isEnabled` off. */
  apiKey: Type7.Optional(Type7.String({ maxLength: 200 })),
  model: Type7.Optional(Type7.String({ minLength: 1, maxLength: 100 })),
  /** 100 disables acting entirely without switching the analysis off. */
  autoActionConfidence: Type7.Optional(Type7.Integer({ minimum: 0, maximum: 100 })),
  chatAnalysisEnabled: Type7.Optional(Type7.Boolean()),
  /** See the notes on these two in `GeminiSettings`. Both default off. */
  autoAnalyseCalls: Type7.Optional(Type7.Boolean()),
  autoAnalyseChats: Type7.Optional(Type7.Boolean()),
  dailyChatCap: Type7.Optional(Type7.Integer({ minimum: 0, maximum: 1e4 })),
  chatAnalysisProvider: Type7.Optional(Type7.Union([Type7.Literal("gemini"), Type7.Literal("claude")])),
  claudeApiKey: Type7.Optional(Type7.String({ maxLength: 500 })),
  claudeModel: Type7.Optional(Type7.String({ minLength: 1, maxLength: 100 })),
  dailyAudioMinuteCap: Type7.Optional(Type7.Integer({ minimum: 0, maximum: 1e5 })),
  minimumDurationSeconds: Type7.Optional(Type7.Integer({ minimum: 0, maximum: 3600 }))
});
var ClearCallReviewResponse = Type7.Object({
  callId: Type7.Integer(),
  reviewedAt: Type7.String()
});

// src/briefing.ts
var WAITING_ON = ["us", "them", "nobody"];
var WAITING_ON_LABELS = {
  us: "Waiting on us",
  them: "Waiting on them",
  nobody: "No messages"
};
var ConversationStats = Type8.Object({
  messages: Type8.Integer(),
  inbound: Type8.Integer(),
  outbound: Type8.Integer(),
  firstAt: Type8.Union([Type8.String(), Type8.Null()]),
  lastAt: Type8.Union([Type8.String(), Type8.Null()]),
  waitingOn: enumOf(WAITING_ON),
  /** Hours since the last message, so "waiting on us" can carry a weight. */
  hoursSinceLast: Type8.Union([Type8.Integer(), Type8.Null()]),
  /** Our share of the messages. The chat equivalent of the call talk ratio. */
  agentSharePercent: Type8.Union([Type8.Integer(), Type8.Null()]),
  /** Typical minutes for THEM to answer US. A rising number is a churn signal. */
  customerMedianReplyMinutes: Type8.Union([Type8.Integer(), Type8.Null()]),
  /** Typical minutes for US to answer THEM. The number we control. */
  ourMedianReplyMinutes: Type8.Union([Type8.Integer(), Type8.Null()])
});
var PromiseMade = Type8.Object({
  callId: Type8.Integer(),
  at: Type8.String(),
  promised: Type8.String()
});
var BriefingLead = Type8.Object({
  id: Type8.String({ format: "uuid" }),
  name: Type8.String(),
  leadStatus: enumOf(LEAD_STATUSES),
  leadRating: Type8.Union([enumOf(LEAD_RATINGS), Type8.Null()]),
  ownerName: Type8.Union([Type8.String(), Type8.Null()])
});
var BriefingDeal = Type8.Object({
  id: Type8.String({ format: "uuid" }),
  title: Type8.String(),
  valueAmount: Type8.Union([Type8.Number(), Type8.Null()]),
  currency: Type8.String()
});
var BestCallHour = Type8.Object({
  /** 0-23, in the app timezone. */
  hour: Type8.Integer(),
  dials: Type8.Integer(),
  answered: Type8.Integer(),
  scope: Type8.Union([Type8.Literal("contact"), Type8.Literal("everyone")])
});
var HowToOpen = Type8.Object({
  demeanour: enumOf(CALL_DEMEANOURS),
  note: Type8.Union([Type8.String(), Type8.Null()]),
  at: Type8.String()
});
var BriefingFlags = Type8.Object({
  /** Calls flagged for a manager and not yet listened to. */
  openReviews: Type8.Integer(),
  latestReviewReason: Type8.Union([enumOf(CALL_REVIEW_REASONS), Type8.Null()]),
  /** The worst risk level on any analysed call, not an average of them. */
  riskLevel: Type8.Union([enumOf(CALL_RISK_LEVELS), Type8.Null()]),
  /** The most recent alternative they named, in their own words. */
  competitorMentioned: Type8.Union([Type8.String(), Type8.Null()])
});
var ContactBriefing = Type8.Object({
  contactId: Type8.String({ format: "uuid" }),
  fullName: Type8.Union([Type8.String(), Type8.Null()]),
  /** The latest of any call, message or recorded activity. */
  lastContactAt: Type8.Union([Type8.String(), Type8.Null()]),
  daysSinceContact: Type8.Union([Type8.Integer(), Type8.Null()]),
  promises: Type8.Array(PromiseMade),
  openFollowUps: Type8.Integer(),
  overdueFollowUps: Type8.Integer(),
  nextFollowUpAt: Type8.Union([Type8.String(), Type8.Null()]),
  conversation: ConversationStats,
  leads: Type8.Array(BriefingLead),
  deals: Type8.Array(BriefingDeal),
  callsTotal: Type8.Integer(),
  callsConnected: Type8.Integer(),
  howToOpen: Type8.Union([HowToOpen, Type8.Null()]),
  /** Null when there is not enough history anywhere to say. */
  bestCallHour: Type8.Union([BestCallHour, Type8.Null()]),
  flags: BriefingFlags
});
var BEST_HOUR_MIN_CALLS = 5;
var BRIEFING_PROMISE_DAYS = 90;
var BRIEFING_PROMISE_LIMIT = 5;

// src/call-queue.ts
import { Type as Type9 } from "@sinclair/typebox";
var CallQueueQuery = Type9.Composite([
  PageQuery,
  Type9.Object({
    /** One bucket, when somebody is working the urgent ones only. */
    priority: Type9.Optional(enumOf(LEAD_PRIORITIES)),
    /** The manager's pass: calls flagged for a human ear and not yet cleared. */
    needsAttention: Type9.Optional(Type9.Boolean()),
    sentiment: Type9.Optional(enumOf(CALL_SENTIMENTS)),
    ownerId: Type9.Optional(Type9.String({ format: "uuid" }))
  })
]);
var QueuedCall = Type9.Object({
  callId: Type9.Integer(),
  startedAt: Type9.String({ format: "date-time" }),
  durationSeconds: Type9.Integer(),
  contactId: Type9.String({ format: "uuid" }),
  contactName: Type9.Union([Type9.String(), Type9.Null()]),
  contactPhone: Type9.Union([Type9.String(), Type9.Null()]),
  /** The enquiry this call was filed against, when it was filed at all. */
  leadId: Type9.Union([Type9.String({ format: "uuid" }), Type9.Null()]),
  leadName: Type9.Union([Type9.String(), Type9.Null()]),
  /** Read through the lead's owner — `crm.calls` records no operator. */
  agentName: Type9.Union([Type9.String(), Type9.Null()]),
  leadPriority: Type9.Union([enumOf(LEAD_PRIORITIES), Type9.Null()]),
  conversionProbability: Type9.Union([Type9.Integer(), Type9.Null()]),
  callScore: Type9.Union([Type9.Integer(), Type9.Null()]),
  sentiment: Type9.Union([enumOf(CALL_SENTIMENTS), Type9.Null()]),
  /** One line. The row says what to do; the panel says why. */
  nextBestAction: Type9.Union([Type9.String(), Type9.Null()]),
  needsAttention: Type9.Boolean()
});
var CallQueuePage = pageOf(QueuedCall);

// src/calls.ts
import { Type as Type13 } from "@sinclair/typebox";

// src/dashboard.ts
import { Type as Type12 } from "@sinclair/typebox";

// src/contacts.ts
import { Type as Type10 } from "@sinclair/typebox";
var ACCOUNT_STATUSES = ["active_paid", "active_payment_pending", "active_expired", "inactive"];
var CREATED_VIA = ["form", "frejun", "import", "api"];
var SELLER_FLAGS = ["followup", "subscription_expiry"];
var ACCOUNT_STATUS_LABELS = {
  active_paid: "Active \u2014 Paid",
  active_payment_pending: "Active \u2014 Payment pending",
  active_expired: "Active \u2014 Expired",
  inactive: "Inactive"
};
var SELLER_FLAG_LABELS = {
  followup: "Follow-up",
  subscription_expiry: "Subscription expiry"
};
var Contact = Type10.Object({
  id: Type10.String({ format: "uuid" }),
  /** Null when the customer arrived as a name alone. They cannot be rung. */
  phoneE164: Type10.Union([PhoneE164, Type10.Null()]),
  /** A second number for the same person. Not the identity — see the schema. */
  altPhoneE164: Type10.Union([PhoneE164, Type10.Null()]),
  fullName: Type10.Union([Type10.String(), Type10.Null()]),
  email: Type10.Union([Type10.String(), Type10.Null()]),
  companyId: Type10.Union([Type10.String({ format: "uuid" }), Type10.Null()]),
  /**
   * Joined, not stored. The §4 header has to read "Chennai Pet Supplies", and a
   * uuid is not something an operator can recognise — so the name travels with
   * the record rather than costing every screen a second request.
   */
  companyName: Type10.Union([Type10.String(), Type10.Null()]),
  designation: Type10.Union([Type10.String(), Type10.Null()]),
  accountStatus: Type10.Union([enumOf(ACCOUNT_STATUSES), Type10.Null()]),
  subscriptionExpiresOn: Type10.Union([Type10.String(), Type10.Null()]),
  platformUserId: Type10.Union([Type10.String(), Type10.Null()]),
  sellerFlag: Type10.Union([enumOf(SELLER_FLAGS), Type10.Null()]),
  sellerNote: Type10.Union([Type10.String(), Type10.Null()]),
  whatsappOptIn: Type10.Boolean(),
  doNotContact: Type10.Boolean(),
  ownerId: Type10.Union([Type10.String({ format: "uuid" }), Type10.Null()]),
  createdVia: enumOf(CREATED_VIA),
  createdAt: Type10.String({ format: "date-time" }),
  updatedAt: Type10.String({ format: "date-time" })
});
var ContactPage = pageOf(Contact);
var CONTACT_SORTS = ["created_desc", "created_asc", "name_asc", "name_desc", "expiry_asc"];
var ListContactsQuery = Type10.Composite([
  PageQuery,
  Type10.Object({
    search: Type10.Optional(Type10.String({ maxLength: 120 })),
    accountStatus: Type10.Optional(enumOf(ACCOUNT_STATUSES)),
    sellerFlag: Type10.Optional(enumOf(SELLER_FLAGS)),
    ownerId: Type10.Optional(Type10.String({ format: "uuid" })),
    /** §18: sellers whose subscription lapses on or before this date. */
    expiringBefore: Type10.Optional(DateOnly),
    /**
     * The other end of the window. With `expiringBefore` it expresses §18's
     * "expiring in the next 30 days" — which `expiringBefore` alone cannot,
     * because a subscription that lapsed in March is also before next month
     * and would swamp the list the filter exists to produce.
     */
    expiringFrom: Type10.Optional(DateOnly),
    /**
     * §17/§18's seller list: only contacts that ARE registered sellers, i.e.
     * carry an account status. A prospect with no ePettyshop account has no
     * subscription to expire and does not belong on that screen.
     */
    sellersOnly: Type10.Optional(Type10.Boolean()),
    /**
     * Only contacts with a call the analyser flagged for a human to hear.
     *
     * Cleared the same way the Needs Attention queue clears one — by a
     * manager stamping `reviewed_at` — so this filter and that queue cannot
     * come to disagree about what is still outstanding.
     */
    needsAttention: Type10.Optional(Type10.Boolean()),
    sort: Type10.Optional(enumOf(CONTACT_SORTS))
  })
]);
var contactWritable = {
  altPhoneE164: Type10.Optional(PhoneE164Input),
  fullName: Type10.Optional(Type10.String({ minLength: 1, maxLength: 200 })),
  email: Type10.Optional(Type10.String({ format: "email", maxLength: 254 })),
  companyId: Type10.Optional(Type10.String({ format: "uuid" })),
  designation: Type10.Optional(Type10.String({ maxLength: 120 })),
  accountStatus: Type10.Optional(enumOf(ACCOUNT_STATUSES)),
  subscriptionExpiresOn: Type10.Optional(DateOnly),
  platformUserId: Type10.Optional(Type10.String({ maxLength: 120 })),
  sellerFlag: Type10.Optional(enumOf(SELLER_FLAGS)),
  sellerNote: Type10.Optional(Type10.String({ maxLength: 2e3 })),
  whatsappOptIn: Type10.Optional(Type10.Boolean()),
  doNotContact: Type10.Optional(Type10.Boolean()),
  ownerId: Type10.Optional(Type10.String({ format: "uuid" }))
};
var CreateContactBody = Type10.Object({ phoneE164: Type10.Optional(PhoneE164Input), ...contactWritable });
var UpdateContactBody = Type10.Partial(
  Type10.Object({
    /**
     * Settable ONLY while it is null — filling in a number you have since
     * learned. Changing an existing one would silently repoint every call,
     * message and lead already hanging off this person, and the API refuses
     * that. There is no null here: a number, once known, cannot be unknown.
     */
    phoneE164: PhoneE164Input,
    /** Editable, unlike the primary: nothing hangs off it. */
    altPhoneE164: Type10.Union([PhoneE164Input, Type10.Null()]),
    fullName: Type10.Union([Type10.String({ minLength: 1, maxLength: 200 }), Type10.Null()]),
    email: Type10.Union([Type10.String({ format: "email", maxLength: 254 }), Type10.Null()]),
    companyId: Type10.Union([Type10.String({ format: "uuid" }), Type10.Null()]),
    designation: Type10.Union([Type10.String({ maxLength: 120 }), Type10.Null()]),
    accountStatus: Type10.Union([enumOf(ACCOUNT_STATUSES), Type10.Null()]),
    subscriptionExpiresOn: Type10.Union([DateOnly, Type10.Null()]),
    platformUserId: Type10.Union([Type10.String({ maxLength: 120 }), Type10.Null()]),
    sellerFlag: Type10.Union([enumOf(SELLER_FLAGS), Type10.Null()]),
    sellerNote: Type10.Union([Type10.String({ maxLength: 2e3 }), Type10.Null()]),
    whatsappOptIn: Type10.Boolean(),
    doNotContact: Type10.Boolean(),
    ownerId: Type10.Union([Type10.String({ format: "uuid" }), Type10.Null()])
  })
);
var DuplicateContactResponse = Type10.Object({
  error: Type10.Literal("duplicate_phone"),
  message: Type10.String(),
  existingContactId: Type10.String({ format: "uuid" })
});

// src/follow-ups.ts
import { Type as Type11 } from "@sinclair/typebox";
var FOLLOW_UP_STATUSES = ["pending", "completed", "missed", "cancelled"];
var FOLLOW_UP_TYPE_LABELS = {
  call: "Call",
  whatsapp: "WhatsApp",
  meeting: "Meeting",
  demo: "Demo",
  discussion: "Discussion",
  other: "Other"
};
var FOLLOW_UP_PRIORITY_LABELS = {
  high: "High",
  normal: "Normal",
  low: "Low"
};
var FOLLOW_UP_STATUS_LABELS = {
  pending: "Pending",
  completed: "Completed",
  missed: "Missed",
  cancelled: "Cancelled"
};
var REMIND_LEAD_MINUTES = [0, 10, 60, 1440];
var REMIND_LEAD_LABELS = {
  0: "At the time it is due",
  10: "10 minutes before",
  60: "1 hour before",
  1440: "1 day before"
};
var DEFAULT_REMIND_BEFORE_MINUTES = 10;
var FollowUp = Type11.Object({
  id: Type11.String({ format: "uuid" }),
  contactId: Type11.String({ format: "uuid" }),
  contactName: Type11.Union([Type11.String(), Type11.Null()]),
  /** Null when the customer has no number — see contacts.phoneE164. */
  contactPhone: Type11.Union([Type11.String(), Type11.Null()]),
  /** Blocks the call and message actions wherever this row is shown. A list
   *  that offers to ring a do-not-contact customer is a compliance bug. */
  doNotContact: Type11.Boolean(),
  /**
   * Which lead this advances. Null is a contact-level follow-up, which is
   * legitimate — a contact may have no lead at all — but it is never a default:
   * a contact may hold SEVERAL open leads, so the operator picks explicitly.
   */
  leadId: Type11.Union([Type11.String({ format: "uuid" }), Type11.Null()]),
  leadName: Type11.Union([Type11.String(), Type11.Null()]),
  leadStatus: Type11.Union([enumOf(LEAD_STATUSES), Type11.Null()]),
  dueAt: Type11.String({ format: "date-time" }),
  /**
   * The calendar day `dueAt` falls on in the app timezone, as YYYY-MM-DD.
   *
   * Computed by Postgres with `at time zone`, not by the browser. Every day
   * boundary in §11 is Asia/Kolkata, and a client that re-derived it from the
   * instant would bucket a 02:00 IST follow-up into the previous day for any
   * viewer whose machine is on another zone.
   */
  dueOn: DateOnly,
  /** Minutes past midnight of `dueOn`, app timezone. Places the week grid row. */
  dueMinutes: Type11.Integer({ minimum: 0, maximum: 1439 }),
  followUpType: enumOf(FOLLOW_UP_TYPES),
  priority: enumOf(FOLLOW_UP_PRIORITIES),
  status: enumOf(FOLLOW_UP_STATUSES),
  note: Type11.Union([Type11.String(), Type11.Null()]),
  remindAt: Type11.Union([Type11.String({ format: "date-time" }), Type11.Null()]),
  completedAt: Type11.Union([Type11.String({ format: "date-time" }), Type11.Null()]),
  ownerId: Type11.Union([Type11.String({ format: "uuid" }), Type11.Null()]),
  ownerName: Type11.Union([Type11.String(), Type11.Null()]),
  /**
   * Derived server-side, never stored: pending AND past due. Computing it here
   * means every client agrees, and it cannot go stale between sweeps.
   */
  isOverdue: Type11.Boolean()
});
var FollowUpPage = pageOf(FollowUp);
var FOLLOW_UP_BUCKETS = ["overdue", "today", "upcoming", "completed", "all"];
var ListFollowUpsQuery = Type11.Composite([
  PageQuery,
  Type11.Object({
    bucket: Type11.Optional(enumOf(FOLLOW_UP_BUCKETS)),
    contactId: Type11.Optional(Type11.String({ format: "uuid" })),
    leadId: Type11.Optional(Type11.String({ format: "uuid" })),
    mineOnly: Type11.Optional(Type11.Boolean())
  })
]);
var FOLLOW_UP_SLOT_MINUTES = 5;
var FreeSlotsQuery = Type11.Object({
  date: DateOnly,
  /**
   * Whose diary. Omitted means YOUR OWN — the same default `createFollowUp`
   * applies when a body arrives with no owner, so the day the dialog shows is
   * the day the save will be checked against.
   *
   * It does NOT mean the unassigned queue; that is `unassigned`, because the
   * two have to be distinguishable. A reschedule of an unowned follow-up asks
   * about the queue, and an omitted owner answering "your diary" there would
   * show one person's clashes and have the server check another's.
   */
  ownerId: Type11.Optional(Type11.String({ format: "uuid" })),
  /**
   * Ask about the unassigned queue instead of a person.
   *
   * Unowned follow-ups collide with each other — whoever works the queue has
   * one pair of hands — so the queue is a diary like any other, just one
   * without a `ownerId` to name it. Wins over `ownerId` if both are sent.
   */
  unassigned: Type11.Optional(Type11.Boolean()),
  /**
   * Ignore this follow-up when deciding what is taken.
   *
   * Set while RESCHEDULING: without it a follow-up collides with its own
   * current slot, and the only time you could not move it to is the one it
   * already has.
   */
  excludeFollowUpId: Type11.Optional(Type11.String({ format: "uuid" }))
});
var DaySlot = Type11.Object({
  /** 'HH:MM', 24-hour, in the app timezone. */
  time: Type11.String(),
  taken: Type11.Boolean()
});
var DaySlots = Type11.Object({
  date: DateOnly,
  slots: Type11.Array(DaySlot),
  /** So the UI can say "nothing free that day" rather than showing an empty list. */
  freeCount: Type11.Integer()
});
var FollowUpCalendarQuery = Type11.Object({
  from: DateOnly,
  to: DateOnly,
  /**
   * Pulls in every pending follow-up already past due, whatever its date. The
   * agenda's first group is the working queue and must not lose the ones that
   * slipped out of the window months ago.
   */
  includeOverdue: Type11.Optional(Type11.Boolean()),
  mineOnly: Type11.Optional(Type11.Boolean()),
  ownerId: Type11.Optional(Type11.String({ format: "uuid" })),
  contactId: Type11.Optional(Type11.String({ format: "uuid" })),
  leadId: Type11.Optional(Type11.String({ format: "uuid" })),
  limit: Type11.Optional(Type11.Integer({ minimum: 1, maximum: 1e3, default: 500 }))
});
var FollowUpCalendar = Type11.Object({
  items: Type11.Array(FollowUp),
  /** Rows matching the filter, before the cap — so the UI can say it truncated. */
  total: Type11.Integer(),
  limit: Type11.Integer(),
  /**
   * The server's today, in the app timezone. Overdue / Today / Tomorrow are
   * decided by comparing `dueOn` against this, which keeps the whole grouping
   * a string comparison and out of the browser's clock and zone.
   */
  today: DateOnly,
  timezone: Type11.String()
});
var FollowUpCounts = Type11.Object({
  overdue: Type11.Integer(),
  today: Type11.Integer(),
  upcoming: Type11.Integer(),
  completed: Type11.Integer()
});
var RemindFields = Type11.Object({
  /** Minutes before due. The server turns this into remind_at. */
  remindBeforeMinutes: Type11.Optional(Type11.Integer({ minimum: 0, maximum: 10080 })),
  /** An explicit instant, for "remind me at a particular time" instead. */
  remindAt: Type11.Optional(Type11.String({ format: "date-time" }))
});
var CreateFollowUpBody = Type11.Composite([
  Type11.Object({
    contactId: Type11.String({ format: "uuid" }),
    /**
     * Omitted means "not about a specific lead" — an explicit choice in the
     * UI. It is never filled in for the operator, because a contact may hold
     * several open leads and guessing puts the follow-up on the wrong one.
     */
    leadId: Type11.Optional(Type11.String({ format: "uuid" })),
    dueAt: Type11.String({ format: "date-time" }),
    followUpType: enumOf(FOLLOW_UP_TYPES),
    priority: Type11.Optional(enumOf(FOLLOW_UP_PRIORITIES)),
    note: Type11.Optional(Type11.String({ maxLength: 2e3 })),
    /** Defaults to the signed-in operator when omitted. */
    ownerId: Type11.Optional(Type11.String({ format: "uuid" }))
  }),
  RemindFields
]);
var RescheduleFollowUpBody = Type11.Composite([
  Type11.Object({
    dueAt: Type11.String({ format: "date-time" }),
    followUpType: Type11.Optional(enumOf(FOLLOW_UP_TYPES)),
    priority: Type11.Optional(enumOf(FOLLOW_UP_PRIORITIES)),
    note: Type11.Optional(Type11.Union([Type11.String({ maxLength: 2e3 }), Type11.Null()]))
  }),
  RemindFields
]);
var CompleteFollowUpBody = Type11.Object({
  /**
   * `missed` is the third answer, and it had been documented for months as
   * something a daily job would set. No such job exists, which left an
   * overdue follow-up `pending` for ever — reminded exactly once and never
   * chased again. It is a HUMAN verb now: "they did not show, or I could not
   * reach them", which is the only honest way anybody learns it.
   */
  status: Type11.Union([Type11.Literal("completed"), Type11.Literal("cancelled"), Type11.Literal("missed")]),
  /**
   * What actually happened. Required by the UI for a demo, optional here.
   *
   * Free text rather than an enum: what the next caller reads is the useful
   * artefact, and a no-show and a demo that went well were previously
   * indistinguishable in the record.
   */
  outcome: Type11.Optional(Type11.String({ maxLength: 2e3 }))
});

// src/dashboard.ts
var DashboardFollowUp = Type12.Object({
  id: Type12.String({ format: "uuid" }),
  contactId: Type12.String({ format: "uuid" }),
  contactName: Type12.Union([Type12.String(), Type12.Null()]),
  /** Null when the customer has no number — see contacts.phoneE164. */
  contactPhone: Type12.Union([Type12.String(), Type12.Null()]),
  leadId: Type12.Union([Type12.String({ format: "uuid" }), Type12.Null()]),
  leadName: Type12.Union([Type12.String(), Type12.Null()]),
  dueAt: Type12.String({ format: "date-time" }),
  followUpType: enumOf(FOLLOW_UP_TYPES),
  priority: enumOf(FOLLOW_UP_PRIORITIES),
  note: Type12.Union([Type12.String(), Type12.Null()])
});
var DashboardDay = Type12.Object({
  date: DateOnly,
  items: Type12.Array(DashboardFollowUp)
});
var StaleLead = Type12.Object({
  id: Type12.String({ format: "uuid" }),
  name: Type12.String(),
  contactId: Type12.String({ format: "uuid" }),
  contactName: Type12.Union([Type12.String(), Type12.Null()]),
  leadStatus: enumOf(LEAD_STATUSES),
  lastActivityAt: Type12.Union([Type12.String({ format: "date-time" }), Type12.Null()]),
  createdAt: Type12.String({ format: "date-time" }),
  idleDays: Type12.Integer()
});
var NEXT_UP_DAYS = 7;
var MyOpenLeads = Type12.Object({
  total: Type12.Integer(),
  staleCount: Type12.Integer(),
  /** Capped — this is a nudge list, not the leads screen. */
  stale: Type12.Array(StaleLead)
});
var MyDayQuery = Type12.Object({
  /** Everyone the caller may see, rather than only their own. */
  everyone: Type12.Optional(Type12.Boolean()),
  /** One operator. Ignored when `everyone` is set. */
  ownerId: Type12.Optional(Type12.String({ format: "uuid" }))
});
var MY_DAY_FOLLOW_UP_CAP = 200;
var MyDayResponse = Type12.Object({
  /** Whose day this is, so the heading can say it rather than always "My day". */
  scope: Type12.Union([Type12.Literal("mine"), Type12.Literal("everyone"), Type12.Literal("owner")]),
  /** More than `MY_DAY_FOLLOW_UP_CAP` matched, and the rest are not here. */
  truncated: Type12.Boolean(),
  /** The instant the buckets were computed, so the UI can say how fresh it is. */
  generatedAt: Type12.String({ format: "date-time" }),
  /** Pending and already past due, oldest first — the ones slipping longest. */
  overdue: Type12.Array(DashboardFollowUp),
  /** Still ahead of you today, by time. Disjoint from `overdue` by construction. */
  dueToday: Type12.Array(DashboardFollowUp),
  /** The following seven days, grouped by day. */
  nextUp: Type12.Array(DashboardDay),
  openLeads: MyOpenLeads,
  completedToday: Type12.Integer()
});
var DashboardStatsQuery = Type12.Object({
  from: Type12.Optional(DateOnly),
  to: Type12.Optional(DateOnly),
  /** Absent means everyone — all operators may read all rows. */
  ownerId: Type12.Optional(Type12.String({ format: "uuid" }))
});
var LeadMetrics = Type12.Object({
  total: Type12.Integer(),
  newLeads: Type12.Integer(),
  contacted: Type12.Integer(),
  interested: Type12.Integer(),
  converted: Type12.Integer(),
  lost: Type12.Integer(),
  /** converted ÷ total, 0–1. Zero when there were no leads, never NaN. */
  conversionRate: Type12.Number()
});
var CALL_OUTCOMES = [
  "in_progress",
  "connected",
  "missed",
  "no_answer",
  "busy",
  "wrong_number",
  "voicemail"
];
var UNREACHED_CALL_OUTCOMES = ["no_answer", "busy", "voicemail"];
var CALL_OUTCOME_LABELS = {
  in_progress: "In progress",
  connected: "Connected",
  missed: "Missed",
  no_answer: "No answer",
  busy: "Busy",
  wrong_number: "Wrong number",
  voicemail: "Voicemail"
};
var CallOutcomeCount = Type12.Object({
  outcome: enumOf(CALL_OUTCOMES),
  count: Type12.Integer()
});
var CallMetrics = Type12.Object({
  total: Type12.Integer(),
  connected: Type12.Integer(),
  missed: Type12.Integer(),
  byOutcome: Type12.Array(CallOutcomeCount)
});
var TemplateUsage = Type12.Object({
  name: Type12.String(),
  count: Type12.Integer()
});
var WhatsappMetrics = Type12.Object({
  total: Type12.Integer(),
  templateSent: Type12.Integer(),
  directSent: Type12.Integer(),
  topTemplates: Type12.Array(TemplateUsage)
});
var FollowUpMetrics = Type12.Object({
  dueToday: Type12.Integer(),
  upcoming: Type12.Integer(),
  overdue: Type12.Integer(),
  completed: Type12.Integer()
});
var DashboardStats = Type12.Object({
  /** Echoed back resolved, so the UI can label the period it actually got. */
  from: DateOnly,
  to: DateOnly,
  leads: LeadMetrics,
  calls: CallMetrics,
  whatsapp: WhatsappMetrics,
  followUps: FollowUpMetrics
});
var DASHBOARD_PERIODS = ["this_month", "last_month", "custom"];
var DASHBOARD_PERIOD_LABELS = {
  this_month: "This month",
  last_month: "Last month",
  custom: "Custom"
};
var TARGET_METRICS = ["leads_converted"];
var TARGET_METRIC_LABELS = {
  leads_converted: "Leads converted"
};
var DEFAULT_TARGET_METRIC = "leads_converted";
var MonthOnly = Type12.String({
  pattern: "^\\d{4}-(0[1-9]|1[0-2])$",
  description: "Calendar month, YYYY-MM"
});
var TARGET_VALUE_MAX = 1e6;
var Target = Type12.Object({
  id: Type12.String({ format: "uuid" }),
  ownerId: Type12.String({ format: "uuid" }),
  /** Joined, so a list of targets is readable without a second request. */
  ownerName: Type12.String(),
  ownerEmail: Type12.String(),
  periodStart: DateOnly,
  periodEnd: DateOnly,
  /** The month the two dates above describe, for the form that edits it. */
  month: MonthOnly,
  metric: enumOf(TARGET_METRICS),
  targetValue: Type12.Number(),
  createdAt: Type12.String({ format: "date-time" }),
  updatedAt: Type12.String({ format: "date-time" })
});
var ListTargetsQuery = Type12.Object({
  /** Omitted means the current month in the operator's timezone. */
  month: Type12.Optional(MonthOnly),
  ownerId: Type12.Optional(Type12.String({ format: "uuid" })),
  metric: Type12.Optional(enumOf(TARGET_METRICS))
});
var UpsertTargetBody = Type12.Object({
  ownerId: Type12.String({ format: "uuid" }),
  month: MonthOnly,
  metric: Type12.Optional(enumOf(TARGET_METRICS)),
  targetValue: Type12.Number({ exclusiveMinimum: 0, maximum: TARGET_VALUE_MAX })
});
var TARGET_YEAR_BASES = ["financial", "calendar"];
var TARGET_YEAR_BASIS_LABELS = {
  financial: "Financial year (Apr\u2013Mar)",
  calendar: "Calendar year (Jan\u2013Dec)"
};
var TARGET_YEAR_START_MONTH = { financial: 4, calendar: 1 };
function targetYearMonths(basis, year) {
  const start = TARGET_YEAR_START_MONTH[basis];
  return Array.from({ length: 12 }, (_, i) => {
    const m = start + i;
    const y = year + Math.floor((m - 1) / 12);
    return `${y}-${String((m - 1) % 12 + 1).padStart(2, "0")}`;
  });
}
function targetYearLabel(basis, year) {
  return basis === "calendar" ? String(year) : `FY ${year}\u2013${String((year + 1) % 100).padStart(2, "0")}`;
}
var UpsertTargetYearBody = Type12.Object({
  ownerId: Type12.String({ format: "uuid" }),
  basis: enumOf(TARGET_YEAR_BASES),
  /** The year the basis STARTS in: 2026 with `financial` means Apr 26–Mar 27. */
  year: Type12.Integer({ minimum: 2e3, maximum: 2100 }),
  metric: Type12.Optional(enumOf(TARGET_METRICS)),
  /** Applied to every month with no explicit figure. */
  targetValue: Type12.Number({ exclusiveMinimum: 0, maximum: TARGET_VALUE_MAX }),
  /** Per-month overrides, keyed 'YYYY-MM'. Months outside the year are refused. */
  months: Type12.Optional(
    Type12.Array(
      Type12.Object({
        month: MonthOnly,
        targetValue: Type12.Number({ exclusiveMinimum: 0, maximum: TARGET_VALUE_MAX })
      }),
      { maxItems: 12 }
    )
  )
});
var UpsertTargetYearResponse = Type12.Object({
  created: Type12.Integer(),
  updated: Type12.Integer(),
  targets: Type12.Array(Target)
});
var UpdateTargetBody = Type12.Object({
  targetValue: Type12.Number({ exclusiveMinimum: 0, maximum: TARGET_VALUE_MAX })
});
var NewSeller = Type12.Object({
  contactId: Type12.String({ format: "uuid" }),
  fullName: Type12.Union([Type12.String(), Type12.Null()]),
  phoneE164: Type12.String(),
  email: Type12.Union([Type12.String(), Type12.Null()]),
  accountStatus: Type12.Union([enumOf(ACCOUNT_STATUSES), Type12.Null()]),
  companyName: Type12.Union([Type12.String(), Type12.Null()]),
  createdAt: Type12.String({ format: "date-time" }),
  /** Days since they were added, so "3 days and nobody has called" is plain. */
  ageDays: Type12.Integer(),
  /** Leads they already have. Zero is the whole point of the list. */
  openLeadCount: Type12.Integer()
});
var NewSellersQuery = Type12.Object({
  /** How recent counts as new. */
  days: Type12.Optional(Type12.Integer({ minimum: 1, maximum: 180, default: 30 })),
  limit: Type12.Optional(Type12.Integer({ minimum: 1, maximum: 50, default: 10 })),
  /** Include sellers who already have an open lead. Off by default —
   *  the list exists to surface the ones nobody is working. */
  includeWorked: Type12.Optional(Type12.Boolean())
});
var NewSellersResponse = Type12.Object({
  items: Type12.Array(NewSeller),
  total: Type12.Integer(),
  days: Type12.Integer()
});
var TeamPerformanceQuery = Type12.Object({
  /** Inclusive calendar dates. Omitting both means the current month. */
  from: Type12.Optional(DateOnly),
  to: Type12.Optional(DateOnly)
});
var TeamMemberPerformance = Type12.Object({
  userId: Type12.String({ format: "uuid" }),
  fullName: Type12.String(),
  email: Type12.String(),
  role: enumOf(["admin", "marketing"]),
  /** Null when this person has no target for the period. */
  targetId: Type12.Union([Type12.String({ format: "uuid" }), Type12.Null()]),
  target: Type12.Union([Type12.Number(), Type12.Null()]),
  /** Converted leads in the period. Counted, never stored. */
  achieved: Type12.Integer(),
  /** achieved ÷ target, 0–1 and uncapped above it. Null without a target. */
  progress: Type12.Union([Type12.Number(), Type12.Null()]),
  /** Open + closed: leads created in the period, or touched during it. */
  leadsWorked: Type12.Integer(),
  /** achieved ÷ leadsWorked, 0–1. Zero for an empty period, never NaN. */
  conversionRate: Type12.Number(),
  /** Most recent timeline entry by this person, at any time. */
  lastActivityAt: Type12.Union([Type12.String({ format: "date-time" }), Type12.Null()])
});
var TeamPerformanceTotals = Type12.Object({
  target: Type12.Number(),
  achieved: Type12.Integer(),
  leadsWorked: Type12.Integer(),
  /** How many people actually have one, so the total is readable. */
  withTarget: Type12.Integer()
});
var TeamPerformance = Type12.Object({
  /** Echoed back resolved, so the UI labels the period it actually got. */
  from: DateOnly,
  to: DateOnly,
  metric: enumOf(TARGET_METRICS),
  /**
   * Where today sits in the period, 0–100 — the pace marker's position.
   *
   * Computed server-side against the same instants the counts use. A browser
   * left on UTC would otherwise place the marker up to five and a half hours
   * away from the boundary the numbers were counted on.
   */
  pacePercent: Type12.Number(),
  /** Whether the viewer may set targets. Targets are readable by everyone. */
  canEditTargets: Type12.Boolean(),
  viewerId: Type12.String({ format: "uuid" }),
  members: Type12.Array(TeamMemberPerformance),
  totals: TeamPerformanceTotals
});
var MyTargetResponse = Type12.Object({
  from: DateOnly,
  to: DateOnly,
  metric: enumOf(TARGET_METRICS),
  /** Null means no target for the period — not a target of zero. */
  target: Type12.Union([Type12.Number(), Type12.Null()]),
  achieved: Type12.Integer(),
  pacePercent: Type12.Number()
});

// src/calls.ts
var CALL_DIRECTIONS = ["inbound", "outbound"];
var CALL_DIRECTION_LABELS = {
  inbound: "Incoming",
  outbound: "Outgoing"
};
var CALL_NOTE_MAX = 4e3;
var CALL_DURATION_MAX_SECONDS = 86400;
var Call = Type13.Object({
  id: Type13.Integer(),
  contactId: Type13.String({ format: "uuid" }),
  contactName: Type13.Union([Type13.String(), Type13.Null()]),
  /** Null when the customer has no number — see contacts.phoneE164. */
  contactPhone: Type13.Union([Type13.String(), Type13.Null()]),
  leadId: Type13.Union([Type13.String({ format: "uuid" }), Type13.Null()]),
  leadName: Type13.Union([Type13.String(), Type13.Null()]),
  /** FreJun's id. Null for a hand-logged call — there is no provider record. */
  externalCallId: Type13.Union([Type13.String(), Type13.Null()]),
  direction: enumOf(CALL_DIRECTIONS),
  outcome: enumOf(CALL_OUTCOMES),
  startedAt: Type13.String({ format: "date-time" }),
  /**
   * When somebody actually picked up, from FreJun's `answer_time`.
   *
   * Null is the common case and means nobody did. It is the honest test for
   * "connected": FreJun reports `Call completed` when the call LEG ends,
   * which an unanswered call does too, and duration is 0 for a call that was
   * answered and hung up on immediately.
   */
  answeredAt: Type13.Union([Type13.String({ format: "date-time" }), Type13.Null()]),
  durationSeconds: Type13.Integer(),
  /** Recordings stay in FreJun; this is a pointer, not a copy. */
  recordingUrl: Type13.Union([Type13.String(), Type13.Null()]),
  /** The §3 note written about this call, when there is one. */
  note: Type13.Union([Type13.String(), Type13.Null()]),
  /**
   * A call that did not connect and has not been followed up.
   *
   * BOTH DIRECTIONS. It was inbound-and-missed only, which left the commoner
   * case on no list at all: 62% of outbound dials on this account go
   * unanswered, and because the attempt itself counts as activity the lead
   * did not even read as stale. Fifty-one contacts were dialled one to three
   * times and thirty-nine were never reached.
   *
   * True when the call was inbound and missed, OR outbound and unanswered
   * (no answer, busy, voicemail — not a wrong number, where the next step is
   * to correct it rather than redial); and nobody has spoken to that
   * customer since in either direction; and nothing is scheduled with them.
   *
   * Derived at read time, never stored: the flag is wrong the moment
   * somebody calls back, and a stored one would need every call path to
   * remember to clear it.
   */
  needsCallback: Type13.Boolean(),
  /**
   * The call landed on a contact with more than one open lead and nobody
   * chose. Derived at read time (`leadId is null and the contact has leads`),
   * never stored — a stored flag goes stale the moment the lead is picked.
   */
  needsFiling: Type13.Boolean(),
  /**
   * The analysis flagged this call and nobody has cleared it.
   *
   * On the LIST, unlike everything else the analysis produced, because it is
   * the one field that says "open this". The summary, score and coaching all
   * live behind the dialog — putting them on a page of fifty rows would ship
   * hundreds of kilobytes nobody reads — but a flag you have to open each
   * call to discover is a flag that never gets found.
   */
  needsAttention: Type13.Boolean(),
  /**
   * The analysis's score, when there is one.
   *
   * On the list for the same reason `needsAttention` is: it is a number, so
   * it costs nothing to carry, and it is what makes a run of calls readable
   * at a glance. Null means the call was never analysed OR was analysed
   * before scoring existed — the two are indistinguishable here and neither
   * is worth a second field.
   */
  callScore: Type13.Union([Type13.Integer(), Type13.Null()]),
  createdAt: Type13.String({ format: "date-time" })
});
var CallPage = pageOf(Call);
var CALLBACK_WINDOW_DAYS = 14;
var CALLS_PAGE_MAX = 200;
var ListCallsQuery = Type13.Object({
  contactId: Type13.Optional(Type13.String({ format: "uuid" })),
  /** Narrows to one lead — the lead screen's own call list. */
  leadId: Type13.Optional(Type13.String({ format: "uuid" })),
  outcome: Type13.Optional(enumOf(CALL_OUTCOMES)),
  direction: Type13.Optional(enumOf(CALL_DIRECTIONS)),
  /** Only the ones nobody has filed against a lead yet. */
  needsFiling: Type13.Optional(Type13.Boolean()),
  /**
   * Only the calls that did not connect and have not been followed up.
   *
   * The whole reason this page exists: somebody did not get through — them
   * to us, or us to them — and nothing has happened since. The inbound ones
   * overwhelmingly arrive outside
   * working hours, when there is nobody to see the banner.
   */
  needsCallback: Type13.Optional(Type13.Boolean()),
  /**
   * Only calls with a call the analyser flagged for a human to hear.
   *
   * Cleared the same way the Needs Attention queue clears one — by a
   * manager stamping `reviewed_at` — so this filter and that queue cannot
   * come to disagree about what is still outstanding.
   */
  needsAttention: Type13.Optional(Type13.Boolean()),
  /** Inclusive, YYYY-MM-DD, read in the app timezone. */
  from: Type13.Optional(DateOnly),
  to: Type13.Optional(DateOnly),
  limit: Type13.Optional(Type13.Integer({ minimum: 1, maximum: CALLS_PAGE_MAX, default: 50 })),
  offset: Type13.Optional(Type13.Integer({ minimum: 0, default: 0 }))
});
var LogCallBody = Type13.Object({
  contactId: Type13.String({ format: "uuid" }),
  leadId: Type13.Optional(Type13.String({ format: "uuid" })),
  direction: enumOf(CALL_DIRECTIONS),
  outcome: enumOf(CALL_OUTCOMES),
  /** When the call happened, which is not when it is being written down. */
  startedAt: Type13.String({ format: "date-time" }),
  durationSeconds: Type13.Integer({ minimum: 0, maximum: CALL_DURATION_MAX_SECONDS }),
  /** Written as a `notes` row carrying `call_id`, never a column on the call. */
  note: Type13.Optional(Type13.String({ minLength: 1, maxLength: CALL_NOTE_MAX }))
});
var UpdateCallBody = Type13.Object({
  leadId: Type13.Union([Type13.String({ format: "uuid" }), Type13.Null()])
});
var DialBody = Type13.Object({});
var DIAL_RESULTS = ["dialing", "fallback", "not_configured"];
var DialResponse = Type13.Object({
  status: enumOf(DIAL_RESULTS),
  /** Always present. The UI opens it whenever `status` is not `dialing`. */
  telHref: Type13.String(),
  /** Shown to the operator as-is — it explains a fallback in their words. */
  message: Type13.String(),
  /**
   * FreJun's id for the call it just placed, when it returns one. The webhook
   * that follows carries the same id, which is what keeps the two from
   * producing two rows.
   */
  externalCallId: Type13.Union([Type13.String(), Type13.Null()]),
  /**
   * Our own row for the call just placed, when one could be written.
   *
   * L16. The call used to exist only if the webhook arrived, which is how 82
   * calls ended up stuck at `in_progress` with no outcome and nobody able to
   * say whether they had happened. With a row written at dial time, a lost
   * webhook is a visible gap rather than a call that never existed — and the
   * post-call dialog has something to follow.
   *
   * Null when the provider returned no id (nothing to reconcile against) or
   * when the dial fell back to the handset.
   */
  callId: Type13.Union([Type13.Integer(), Type13.Null()])
});
var FrejunIngestResponse = Type13.Object({
  status: enumOf(["created", "duplicate"]),
  callId: Type13.Integer(),
  contactId: Type13.String({ format: "uuid" }),
  leadId: Type13.Union([Type13.String({ format: "uuid" }), Type13.Null()]),
  /** True when a contact was created by this delivery rather than matched. */
  contactCreated: Type13.Boolean(),
  /** True when a lead was opened because the contact had no live one. */
  leadCreated: Type13.Boolean()
});
var CallHourStat = Type13.Object({
  /** 0–23 in the app timezone, not UTC. */
  hour: Type13.Integer({ minimum: 0, maximum: 23 }),
  dials: Type13.Integer(),
  answered: Type13.Integer()
});
var CallHourStatsResponse = Type13.Object({
  hours: Type13.Array(CallHourStat),
  /** The hour to aim at, or null when no hour has enough history. */
  bestHour: Type13.Union([Type13.Integer({ minimum: 0, maximum: 23 }), Type13.Null()])
});

// src/chat-analysis.ts
import { Type as Type14 } from "@sinclair/typebox";
var ChatAnalysis = Type14.Object({
  contactId: Type14.String({ format: "uuid" }),
  status: enumOf(CALL_ANALYSIS_STATUSES),
  model: Type14.Union([Type14.String(), Type14.Null()]),
  analysedAt: Type14.Union([Type14.String(), Type14.Null()]),
  failureReason: Type14.Union([Type14.String(), Type14.Null()]),
  /**
   * How far the analysis reached.
   *
   * `isStale` is the useful one for a reader: it says the conversation has
   * moved on since this was written, which is the question somebody looking
   * at a three-week-old summary actually has.
   */
  messageCount: Type14.Union([Type14.Integer(), Type14.Null()]),
  firstMessageAt: Type14.Union([Type14.String(), Type14.Null()]),
  lastMessageAt: Type14.Union([Type14.String(), Type14.Null()]),
  truncated: Type14.Boolean(),
  isStale: Type14.Boolean(),
  summary: Type14.Union([Type14.String(), Type14.Null()]),
  sentiment: Type14.Union([enumOf(CALL_SENTIMENTS), Type14.Null()]),
  sentimentNote: Type14.Union([Type14.String(), Type14.Null()]),
  potential: Type14.Union([enumOf(LEAD_RATINGS), Type14.Null()]),
  potentialNote: Type14.Union([Type14.String(), Type14.Null()]),
  potentialConfidence: Type14.Union([Type14.Integer(), Type14.Null()]),
  demeanour: Type14.Union([enumOf(CALL_DEMEANOURS), Type14.Null()]),
  demeanourNote: Type14.Union([Type14.String(), Type14.Null()]),
  intent: Type14.Union([enumOf(CALL_INTENTS), Type14.Null()]),
  rootCause: Type14.Union([Type14.String(), Type14.Null()]),
  competitorMentioned: Type14.Union([Type14.String(), Type14.Null()]),
  riskLevel: Type14.Union([enumOf(CALL_RISK_LEVELS), Type14.Null()]),
  riskNote: Type14.Union([Type14.String(), Type14.Null()]),
  nextBestAction: Type14.Union([Type14.String(), Type14.Null()]),
  coachingNote: Type14.Union([Type14.String(), Type14.Null()]),
  productView: Type14.Union([Type14.String(), Type14.Null()]),
  askedFor: Type14.Union([Type14.String(), Type14.Null()]),
  blocker: Type14.Union([Type14.String(), Type14.Null()]),
  promised: Type14.Union([Type14.String(), Type14.Null()]),
  /** The signal a phone call cannot have: silence on a call is audible. */
  unansweredQuestion: Type14.Union([Type14.String(), Type14.Null()]),
  reviewReason: Type14.Union([enumOf(CALL_REVIEW_REASONS), Type14.Null()]),
  reviewNote: Type14.Union([Type14.String(), Type14.Null()]),
  reviewedAt: Type14.Union([Type14.String(), Type14.Null()]),
  followUpAt: Type14.Union([Type14.String(), Type14.Null()]),
  followUpType: Type14.Union([enumOf(FOLLOW_UP_TYPES), Type14.Null()]),
  followUpReason: Type14.Union([Type14.String(), Type14.Null()]),
  followUpConfidence: Type14.Union([Type14.Integer(), Type14.Null()]),
  createdFollowUpId: Type14.Union([Type14.String(), Type14.Null()]),
  appliedRating: Type14.Boolean(),
  /** What the operator asked for, if they asked. Null means the default. */
  requestedProvider: Type14.Union([Type14.String(), Type14.Null()])
});
var ANALYSE_CHAT_RESULTS = ["queued", "already_analysed", "no_messages", "disabled"];
var ANALYSE_CHAT_RESULT_MESSAGES = {
  queued: "Queued. The summary appears here when it is ready.",
  already_analysed: "This conversation has already been analysed.",
  no_messages: "There are no WhatsApp messages on this contact to read.",
  disabled: "Chat analysis is switched off for this organisation."
};
var AnalyseChatBody = Type14.Object({
  /** Re-read the whole thread even if nothing has changed. */
  force: Type14.Optional(Type14.Boolean()),
  /**
   * Which model to use for THIS reading.
   *
   * Chosen where the button is rather than in settings, because that is
   * where somebody is deciding — and because comparing two models on the
   * same conversation is the only honest way to pick one. Omitted means
   * the org's configured default, which is what the unattended sweep uses.
   */
  provider: Type14.Optional(Type14.Union([Type14.Literal("gemini"), Type14.Literal("claude")]))
});
var AnalyseChatResponse = Type14.Object({
  result: enumOf(ANALYSE_CHAT_RESULTS),
  message: Type14.String(),
  analysis: Type14.Union([ChatAnalysis, Type14.Null()])
});
var CHAT_REANALYSE_NEW_MESSAGES = 5;
var CHAT_REANALYSE_AFTER_HOURS = 24;
var CHAT_ANALYSIS_MESSAGE_WINDOW = 1500;
var GEMINI_DAILY_CHAT_CAP_DEFAULT = 100;
var AI_PROVIDERS = ["gemini", "claude"];
var AI_PROVIDER_LABELS = {
  gemini: "Google Gemini",
  claude: "Claude (Haiku)"
};
var AI_PROVIDER_HINTS = {
  gemini: "Shares the daily request allowance with call analysis. On the free tier that is 20 requests a day for both put together.",
  claude: "Cheap and fast for text, and leaves the whole Gemini allowance for call recordings."
};
var CHAT_ANALYSIS_PROVIDER_DEFAULT = "claude";

// src/coaching.ts
import { Type as Type15 } from "@sinclair/typebox";
var CoachingQuery = Type15.Composite([
  PageQuery,
  Type15.Object({
    /**
     * Whose calls, read through the lead's owner.
     *
     * The screen's main use once there is more than one agent: a manager
     * reading one person's week, or an agent reading their own.
     */
    ownerId: Type15.Optional(Type15.String({ format: "uuid" })),
    /** Inclusive, YYYY-MM-DD, read in the app timezone like every other date filter. */
    from: Type15.Optional(DateOnly),
    to: Type15.Optional(DateOnly),
    /**
     * Only calls at or below a score.
     *
     * The useful direction is DOWNWARD. "Show me everything under 60" is a
     * question about where the problems are; "show me everything above 80" is
     * a rosette, and this screen is not for awarding those.
     */
    maxScore: Type15.Optional(Type15.Integer({ minimum: 0, maximum: 100 }))
  })
]);
var CoachedCall = Type15.Object({
  callId: Type15.Integer(),
  startedAt: Type15.String({ format: "date-time" }),
  durationSeconds: Type15.Integer(),
  contactId: Type15.String({ format: "uuid" }),
  contactName: Type15.Union([Type15.String(), Type15.Null()]),
  /** E.164, so the WhatsApp draft's send link works from this screen too. */
  contactPhone: Type15.Union([Type15.String(), Type15.Null()]),
  leadId: Type15.Union([Type15.String({ format: "uuid" }), Type15.Null()]),
  leadName: Type15.Union([Type15.String(), Type15.Null()]),
  /** Null when the call sits on an unowned lead, or on no lead. See the header. */
  agentName: Type15.Union([Type15.String(), Type15.Null()]),
  callScore: Type15.Union([Type15.Integer(), Type15.Null()]),
  agentPitchFeedback: Type15.Union([Type15.String(), Type15.Null()]),
  coachingNote: Type15.Union([Type15.String(), Type15.Null()]),
  /** So a flagged call can be marked as such without a second request. */
  needsAttention: Type15.Boolean()
});
var CoachingPage = Type15.Composite([
  pageOf(CoachedCall),
  Type15.Object({
    averageScore: Type15.Union([Type15.Integer(), Type15.Null()]),
    scoredCount: Type15.Integer()
  })
]);

// src/customer-insight.ts
import { Type as Type16 } from "@sinclair/typebox";
var Sourced = (value) => Type16.Object({
  value,
  /** Which call it came from, so the UI can open that analysis. */
  callId: Type16.Integer(),
  at: Type16.String({ format: "date-time" })
});
var CustomerInsight = Type16.Object({
  contactId: Type16.String({ format: "uuid" }),
  /**
   * How many of this customer's calls have been analysed.
   *
   * Shown on the panel, because an empty one means two very different things:
   * nobody has analysed a call yet, or the calls were analysed and said
   * nothing about the customer. The first has an obvious next step.
   */
  analysedCalls: Type16.Integer(),
  /** Hot / warm / cold, with the sentence that argued for it. */
  potential: Type16.Union([
    Type16.Object({
      value: enumOf(LEAD_RATINGS),
      note: Type16.Union([Type16.String(), Type16.Null()]),
      callId: Type16.Integer(),
      at: Type16.String({ format: "date-time" })
    }),
    Type16.Null()
  ]),
  /** Who on their side can actually say yes. */
  decisionMaker: Type16.Union([Sourced(Type16.String()), Type16.Null()]),
  /** Why they might buy. A checklist, in their own terms. */
  buyingSignals: Type16.Union([Sourced(Type16.Array(Type16.String())), Type16.Null()]),
  /** The one thing holding them back. */
  primaryObjection: Type16.Union([Sourced(Type16.String()), Type16.Null()]),
  /** What they said about the product itself. */
  productView: Type16.Union([Sourced(Type16.String()), Type16.Null()]),
  /** Something they wanted that we do not do — a roadmap input. */
  productGap: Type16.Union([Sourced(Type16.String()), Type16.Null()]),
  /** Who else they named. The competitor nobody has heard of is the useful one. */
  competitor: Type16.Union([Sourced(Type16.String()), Type16.Null()]),
  /** Churn signal, with what gave it. */
  risk: Type16.Union([
    Type16.Object({
      level: enumOf(CALL_RISK_LEVELS),
      note: Type16.Union([Type16.String(), Type16.Null()]),
      callId: Type16.Integer(),
      at: Type16.String({ format: "date-time" })
    }),
    Type16.Null()
  ])
});

// src/data-sources.ts
import { Type as Type17 } from "@sinclair/typebox";
var DATA_SOURCE_KINDS = ["graphql"];
var DATA_SOURCE_AUTHS = ["none", "api_key", "bearer", "oauth_client_credentials"];
var DATA_SOURCE_AUTH_LABELS = {
  none: "No authentication",
  api_key: "API key in a header",
  bearer: "Static bearer token",
  oauth_client_credentials: "OAuth client credentials"
};
var DATA_SOURCE_AUTH_HINTS = {
  none: "The endpoint is open, or restricted some other way.",
  api_key: "A long-lived key sent on every request. No token step.",
  bearer: "A token you paste in, sent as Authorization: Bearer. No token step.",
  oauth_client_credentials: "A client ID and secret exchanged for a short-lived token, refreshed on expiry."
};
var DATA_SOURCE_FIELD_TYPES = [
  "text",
  "number",
  "date",
  "datetime",
  "phone",
  "email",
  "badge",
  "boolean"
];
var DATA_SOURCE_FILTER_TYPES = ["text", "number", "select", "date", "boolean"];
var DataSourceColumn = Type17.Object({
  key: Type17.String({ minLength: 1, maxLength: 60 }),
  label: Type17.String({ minLength: 1, maxLength: 60 }),
  path: Type17.String({ minLength: 1, maxLength: 200 }),
  type: Type17.Optional(enumOf(DATA_SOURCE_FIELD_TYPES)),
  /** The one that carries the row's name, shown first and in bold. */
  primary: Type17.Optional(Type17.Boolean())
});
var DataSourceFilter = Type17.Object({
  key: Type17.String({ minLength: 1, maxLength: 60 }),
  label: Type17.String({ minLength: 1, maxLength: 60 }),
  type: enumOf(DATA_SOURCE_FILTER_TYPES),
  variable: Type17.String({ minLength: 1, maxLength: 60 }),
  placeholder: Type17.Optional(Type17.String({ maxLength: 80 })),
  options: Type17.Optional(
    Type17.Array(Type17.Object({ value: Type17.String({ maxLength: 120 }), label: Type17.String({ maxLength: 120 }) }), {
      maxItems: 50
    })
  )
});
var DataSourceIdentity = Type17.Partial(
  Type17.Object({
    phonePath: Type17.String({ maxLength: 200 }),
    namePath: Type17.String({ maxLength: 200 }),
    emailPath: Type17.String({ maxLength: 200 }),
    externalIdPath: Type17.String({ maxLength: 200 })
  })
);
var DataSourceDisplay = Type17.Partial(
  Type17.Object({
    showInSidebar: Type17.Boolean(),
    sidebarOrder: Type17.Integer({ minimum: 0, maximum: 99 }),
    showOnDashboard: Type17.Boolean(),
    dashboardTitle: Type17.String({ maxLength: 60 }),
    dashboardLimit: Type17.Integer({ minimum: 1, maximum: 20 })
  })
);
var DataSource = Type17.Object({
  id: Type17.String({ format: "uuid" }),
  key: Type17.String(),
  label: Type17.String(),
  description: Type17.Union([Type17.String(), Type17.Null()]),
  kind: enumOf(DATA_SOURCE_KINDS),
  endpointUrl: Type17.String(),
  authType: enumOf(DATA_SOURCE_AUTHS),
  hasCredentials: Type17.Boolean(),
  credentialHint: Type17.Union([Type17.String(), Type17.Null()]),
  tokenTtlSeconds: Type17.Integer(),
  tokenExpiresAt: Type17.Union([Type17.String({ format: "date-time" }), Type17.Null()]),
  query: Type17.String(),
  itemsPath: Type17.String(),
  totalPath: Type17.Union([Type17.String(), Type17.Null()]),
  columns: Type17.Array(DataSourceColumn),
  filters: Type17.Array(DataSourceFilter),
  identity: DataSourceIdentity,
  display: DataSourceDisplay,
  isEnabled: Type17.Boolean(),
  lastCheckedAt: Type17.Union([Type17.String({ format: "date-time" }), Type17.Null()]),
  lastCheckOk: Type17.Union([Type17.Boolean(), Type17.Null()]),
  lastCheckMessage: Type17.Union([Type17.String(), Type17.Null()])
});
var DataSourceSummary = Type17.Object({
  key: Type17.String(),
  label: Type17.String(),
  description: Type17.Union([Type17.String(), Type17.Null()]),
  columns: Type17.Array(DataSourceColumn),
  filters: Type17.Array(DataSourceFilter),
  display: DataSourceDisplay,
  canOpenEnquiry: Type17.Boolean(),
  canLinkContact: Type17.Boolean()
});
var writable = {
  key: Type17.String({ minLength: 2, maxLength: 40, pattern: "^[a-z0-9][a-z0-9-]{1,40}$" }),
  label: Type17.String({ minLength: 1, maxLength: 60 }),
  description: Type17.Union([Type17.String({ maxLength: 300 }), Type17.Null()]),
  endpointUrl: Type17.String({ minLength: 1, maxLength: 500 }),
  authType: enumOf(DATA_SOURCE_AUTHS),
  tokenTtlSeconds: Type17.Integer({ minimum: 60, maximum: 86400 }),
  query: Type17.String({ minLength: 1, maxLength: 8e3 }),
  itemsPath: Type17.String({ minLength: 1, maxLength: 200 }),
  totalPath: Type17.Union([Type17.String({ maxLength: 200 }), Type17.Null()]),
  columns: Type17.Array(DataSourceColumn, { maxItems: 30 }),
  filters: Type17.Array(DataSourceFilter, { maxItems: 12 }),
  identity: DataSourceIdentity,
  display: DataSourceDisplay,
  isEnabled: Type17.Boolean(),
  /** Omitted keeps what is stored; an empty string clears it. */
  apiKey: Type17.Union([Type17.String({ maxLength: 500 }), Type17.Null()]),
  apiKeyHeader: Type17.Union([Type17.String({ maxLength: 60 }), Type17.Null()]),
  bearerToken: Type17.Union([Type17.String({ maxLength: 4e3 }), Type17.Null()]),
  clientId: Type17.Union([Type17.String({ maxLength: 200 }), Type17.Null()]),
  clientSecret: Type17.Union([Type17.String({ maxLength: 400 }), Type17.Null()]),
  tokenUrl: Type17.Union([Type17.String({ maxLength: 500 }), Type17.Null()]),
  scope: Type17.Union([Type17.String({ maxLength: 200 }), Type17.Null()])
};
var CreateDataSourceBody = Type17.Composite([
  Type17.Object({
    key: writable.key,
    label: writable.label,
    endpointUrl: writable.endpointUrl,
    query: writable.query,
    itemsPath: writable.itemsPath
  }),
  Type17.Partial(Type17.Object(writable))
]);
var UpdateDataSourceBody = Type17.Partial(Type17.Object(writable));
var QueryDataSourceBody = Type17.Object({
  filters: Type17.Optional(Type17.Record(Type17.String(), Type17.Union([Type17.String(), Type17.Boolean(), Type17.Null()]))),
  limit: Type17.Optional(Type17.Integer({ minimum: 1, maximum: 200 })),
  offset: Type17.Optional(Type17.Integer({ minimum: 0 }))
});
var DataSourceRow = Type17.Object({
  /** Stable enough to key a list on; falls back to the row index. */
  id: Type17.String(),
  values: Type17.Record(Type17.String(), Type17.Union([Type17.String(), Type17.Null()])),
  phoneE164: Type17.Union([Type17.String(), Type17.Null()]),
  name: Type17.Union([Type17.String(), Type17.Null()]),
  email: Type17.Union([Type17.String(), Type17.Null()]),
  externalId: Type17.Union([Type17.String(), Type17.Null()]),
  /** Set when a CRM contact already has this phone — the row links to them. */
  contactId: Type17.Union([Type17.String({ format: "uuid" }), Type17.Null()]),
  openLeadCount: Type17.Integer()
});
var DataSourceResult = Type17.Object({
  rows: Type17.Array(DataSourceRow),
  total: Type17.Union([Type17.Integer(), Type17.Null()]),
  /** How long the upstream call took, so a slow source is visible not guessed. */
  tookMs: Type17.Integer()
});

// src/deals.ts
import { Type as Type18 } from "@sinclair/typebox";
var DEAL_STATUSES = ["open", "won", "lost"];
var DEAL_STATUS_LABELS = {
  open: "Open",
  won: "Won",
  lost: "Lost"
};
var Deal = Type18.Object({
  id: Type18.String({ format: "uuid" }),
  title: Type18.String(),
  primaryContactId: Type18.String({ format: "uuid" }),
  companyId: Type18.Union([Type18.String({ format: "uuid" }), Type18.Null()]),
  pipelineId: Type18.String({ format: "uuid" }),
  pipelineName: Type18.String(),
  stageId: Type18.String({ format: "uuid" }),
  stageName: Type18.String(),
  /** Money. Null means "not quoted yet", which is not the same as zero. */
  valueAmount: Type18.Union([Type18.Number(), Type18.Null()]),
  currency: Type18.String(),
  status: enumOf(DEAL_STATUSES),
  expectedCloseOn: Type18.Union([DateOnly, Type18.Null()]),
  closedAt: Type18.Union([Type18.String({ format: "date-time" }), Type18.Null()]),
  lostReason: Type18.Union([Type18.String(), Type18.Null()]),
  ownerId: Type18.Union([Type18.String({ format: "uuid" }), Type18.Null()]),
  ownerName: Type18.Union([Type18.String(), Type18.Null()]),
  createdAt: Type18.String({ format: "date-time" }),
  updatedAt: Type18.String({ format: "date-time" })
});
var PipelineStageOption = Type18.Object({
  id: Type18.String({ format: "uuid" }),
  name: Type18.String(),
  pipelineId: Type18.String({ format: "uuid" }),
  pipelineName: Type18.String(),
  position: Type18.Integer(),
  probability: Type18.Number(),
  isWon: Type18.Boolean(),
  isLost: Type18.Boolean(),
  /** True for the stages of the default pipeline, which conversion preselects. */
  isDefaultPipeline: Type18.Boolean()
});
var ConvertLeadResponse = Type18.Object({
  lead: Lead,
  deal: Deal
});
var Uuid = Type18.String({ format: "uuid" });
var DealListItem = Type18.Composite([
  Deal,
  Type18.Object({
    contactName: Type18.Union([Type18.String(), Type18.Null()]),
    /** Null when the customer has no number — see contacts.phoneE164. */
    contactPhone: Type18.Union([Type18.String(), Type18.Null()]),
    companyName: Type18.Union([Type18.String(), Type18.Null()]),
    stagePosition: Type18.Integer(),
    stageProbability: Type18.Number()
  })
]);
var DealPage = pageOf(DealListItem);
var DEAL_SORTS = ["created_desc", "created_asc", "updated_desc", "value_desc", "close_asc"];
var DEAL_SORT_LABELS = {
  created_desc: "Newest first",
  created_asc: "Oldest first",
  updated_desc: "Recently updated",
  value_desc: "Largest value",
  close_asc: "Closing soonest"
};
var ListDealsQuery = Type18.Composite([
  PageQuery,
  Type18.Object({
    search: Type18.Optional(Type18.String({ maxLength: 120 })),
    status: Type18.Optional(enumOf(DEAL_STATUSES)),
    pipelineId: Type18.Optional(Uuid),
    stageId: Type18.Optional(Uuid),
    ownerId: Type18.Optional(Uuid),
    primaryContactId: Type18.Optional(Uuid),
    expectedCloseFrom: Type18.Optional(DateOnly),
    expectedCloseTo: Type18.Optional(DateOnly),
    sort: Type18.Optional(enumOf(DEAL_SORTS))
  })
]);
var UpdateDealBody = Type18.Partial(
  Type18.Object({
    title: Type18.String({ minLength: 1, maxLength: 200 }),
    /** Null clears the quote. Not the same as quoting zero. */
    valueAmount: Type18.Union([Type18.Number({ minimum: 0 }), Type18.Null()]),
    expectedCloseOn: Type18.Union([DateOnly, Type18.Null()]),
    ownerId: Type18.Union([Uuid, Type18.Null()])
  })
);
var ChangeDealStageBody = Type18.Object({
  stageId: Uuid,
  /** Required when the target stage is flagged lost; ignored otherwise. */
  lostReason: Type18.Optional(Type18.String({ minLength: 1, maxLength: 500 }))
});
var MarkDealWonBody = Type18.Object({
  stageId: Type18.Optional(Uuid)
});
var MarkDealLostBody = Type18.Object({
  lostReason: Type18.String({ minLength: 1, maxLength: 500 }),
  stageId: Type18.Optional(Uuid)
});
var PipelineSummary = Type18.Object({
  id: Uuid,
  name: Type18.String(),
  isDefault: Type18.Boolean()
});
var DealBoardStage = Type18.Object({
  id: Uuid,
  name: Type18.String(),
  position: Type18.Integer(),
  probability: Type18.Number(),
  isWon: Type18.Boolean(),
  isLost: Type18.Boolean(),
  dealCount: Type18.Integer(),
  totalValue: Type18.Number(),
  /** value × stage probability. The forecast line under the column header. */
  weightedValue: Type18.Number()
});
var DealBoard = Type18.Object({
  pipeline: PipelineSummary,
  /** Every pipeline in the org, so the switcher needs no second request. */
  pipelines: Type18.Array(PipelineSummary),
  stages: Type18.Array(DealBoardStage),
  deals: Type18.Array(DealListItem),
  totals: Type18.Object({
    dealCount: Type18.Integer(),
    totalValue: Type18.Number(),
    weightedValue: Type18.Number()
  }),
  /** True when the card cap hid some deals. The totals still counted them. */
  truncated: Type18.Boolean(),
  currency: Type18.String()
});
var DEAL_BOARD_CARD_CAP = 400;
var DealBoardQuery = Type18.Object({
  pipelineId: Type18.Optional(Uuid),
  ownerId: Type18.Optional(Uuid),
  search: Type18.Optional(Type18.String({ maxLength: 120 })),
  /**
   * Off by default. A board that shows every deal ever won buries the six that
   * are still moveable under a year of history.
   */
  includeClosed: Type18.Optional(Type18.Boolean())
});

// src/errors.ts
import { Type as Type19 } from "@sinclair/typebox";
var ErrorResponse = Type19.Object({
  error: Type19.String({ description: "Stable machine-readable code" }),
  message: Type19.String()
});
var ERROR_CODES = {
  missing_token: "missing_token",
  invalid_token: "invalid_token",
  no_crm_profile: "no_crm_profile",
  account_disabled: "account_disabled",
  /** Authenticated, provisioned, and still not allowed to do this. */
  forbidden: "forbidden",
  not_found: "not_found",
  /**
   * A proposed `manager_id` would put a loop in the reporting tree. The
   * recursive CTE behind every visibility check walks that tree, so a cycle is
   * rejected at the write rather than discovered by a query that never ends.
   */
  manager_cycle: "manager_cycle",
  duplicate_phone: "duplicate_phone",
  /** The contact already has an open lead — one open lead per contact. */
  open_lead_exists: "open_lead_exists",
  /**
   * That owner already has a follow-up in this five-minute slot.
   *
   * Refused rather than warned about: nobody can be on two calls at once, so
   * one of the two has to move whatever the CRM records. Letting both be
   * booked only moves the collision to the moment it cannot be fixed.
   */
  slot_taken: "slot_taken",
  conflict: "conflict",
  validation_failed: "validation_failed",
  /** An integration this deployment has not been given credentials for. */
  not_configured: "not_configured",
  database_unavailable: "database_unavailable",
  internal_error: "internal_error"
};

// src/lead-sheet.ts
import { Type as Type20 } from "@sinclair/typebox";
var LeadArrivalsQuery = Type20.Object({
  /** Inclusive calendar dates in the app timezone. */
  from: DateOnly,
  to: DateOnly,
  ownerId: Type20.Optional(Type20.String({ format: "uuid" })),
  leadSource: Type20.Optional(enumOf(LEAD_SOURCES)),
  /**
   * A cap on ROWS, not days. A quiet fortnight and a busy afternoon both fit;
   * the response says when it truncated so the page can admit it rather than
   * quietly showing a smaller count than the day actually had.
   */
  limit: Type20.Optional(Type20.Integer({ minimum: 1, maximum: 2e3, default: 500 }))
});
var LeadArrival = Type20.Object({
  leadId: Type20.String({ format: "uuid" }),
  name: Type20.String(),
  contactId: Type20.String({ format: "uuid" }),
  contactName: Type20.Union([Type20.String(), Type20.Null()]),
  contactPhone: Type20.Union([Type20.String(), Type20.Null()]),
  leadSource: enumOf(LEAD_SOURCES),
  leadStatus: enumOf(LEAD_STATUSES),
  ownerName: Type20.Union([Type20.String(), Type20.Null()]),
  /** When it arrived, to the minute, for ordering within the day. */
  createdAt: Type20.String({ format: "date-time" }),
  /**
   * The support team's own cell — what this sheet shows and edits.
   *
   * NOT a `crm.notes` row. The two were one store at first and that was
   * wrong in both directions: the sheet's rough jottings became permanent
   * timeline entries, and a note written on the lead screen turned up as
   * though somebody had filled in the sheet.
   */
  supportNote: Type20.Union([Type20.String(), Type20.Null()]),
  /**
   * The newest note from the lead's timeline, read-only.
   *
   * Context, not the sheet's own field — what the customer last said is the
   * reason somebody is writing in the cell beside it.
   */
  latestNote: Type20.Union([Type20.String(), Type20.Null()]),
  /**
   * §9's tags, as chips on the row.
   *
   * The sheet is where a day's arrivals are triaged, so "Interested" and "Not
   * Reachable" belong on it — those are the labels that decide which rows are
   * worth a second call. Shared with the leads list rather than a second
   * labelling system, because a tag applied here has to mean the same thing
   * everywhere it appears.
   */
  tags: Type20.Array(
    Type20.Object({
      id: Type20.String({ format: "uuid" }),
      name: Type20.String(),
      color: Type20.Union([Type20.String(), Type20.Null()])
    })
  )
});
var LeadArrivalDay = Type20.Object({
  date: DateOnly,
  /**
   * How many arrived that day, BEFORE the row cap.
   *
   * Counted in SQL rather than taken from `leads.length`, so a truncated
   * response still reports the real number — the count is the whole point of
   * the page and the one figure that must not quietly shrink.
   */
  count: Type20.Integer(),
  converted: Type20.Integer(),
  leads: Type20.Array(LeadArrival)
});
var LeadArrivals = Type20.Object({
  days: Type20.Array(LeadArrivalDay),
  /** Arrivals in the whole window, before the cap. */
  total: Type20.Integer(),
  truncated: Type20.Boolean()
});

// src/lookups.ts
import { Type as T, Type as Type21 } from "@sinclair/typebox";
var UserOption = Type21.Object({
  id: Type21.String({ format: "uuid" }),
  fullName: Type21.String(),
  email: Type21.String(),
  role: Type21.Union([Type21.Literal("admin"), Type21.Literal("marketing")])
});
var CompanyOption = Type21.Object({
  id: Type21.String({ format: "uuid" }),
  name: Type21.String()
});
var CampaignOption = Type21.Object({
  id: Type21.String({ format: "uuid" }),
  name: Type21.String(),
  /** Lets the lead form show only campaigns matching the chosen source. */
  channel: Type21.Union([Type21.String(), Type21.Null()])
});
var TagOption = Type21.Object({
  id: Type21.String({ format: "uuid" }),
  name: Type21.String(),
  color: Type21.Union([Type21.String(), Type21.Null()])
});
var SearchLookupQuery = T.Object({
  search: T.Optional(T.String({ maxLength: 120 })),
  limit: T.Optional(T.Integer({ minimum: 1, maximum: 50, default: 20 }))
});
var CreateCompanyBody = Type21.Object({
  name: Type21.String({ minLength: 1, maxLength: 200 })
});

// src/needs-attention.ts
import { Type as Type22 } from "@sinclair/typebox";
var ATTENTION_REASONS = [
  "call_review",
  "unanswered_message",
  "no_follow_up",
  "overdue_follow_up",
  "stale"
];
var ATTENTION_REASON_LABELS = {
  call_review: "A call needs listening to",
  unanswered_message: "They messaged and nobody replied",
  no_follow_up: "No next step scheduled",
  overdue_follow_up: "Follow-up overdue",
  stale: "No activity recently"
};
var ATTENTION_REASON_SHORT = {
  call_review: "Review the call",
  unanswered_message: "Nobody replied",
  no_follow_up: "No next step",
  overdue_follow_up: "Overdue",
  stale: "Gone quiet"
};
var ATTENTION_REASON_HINTS = {
  call_review: "Something on a recorded call needs a manager. Listen before anyone rings back.",
  unanswered_message: "A real person wrote to you and got silence. The commonest way a WhatsApp lead dies.",
  no_follow_up: "Nothing is booked on these leads. Schedule the next step now.",
  overdue_follow_up: "The moment you picked has passed. Do it, or move it.",
  stale: "A next step exists, but nothing has happened on these for a while."
};
var ATTENTION_STALE_DAYS = 7;
var ATTENTION_UNANSWERED_HOURS = 24;
var AttentionLead = Type22.Object({
  id: Type22.String({ format: "uuid" }),
  name: Type22.String(),
  contactId: Type22.String({ format: "uuid" }),
  contactName: Type22.Union([Type22.String(), Type22.Null()]),
  /** Null when the customer has no number — see contacts.phoneE164. */
  contactPhone: Type22.Union([Type22.String(), Type22.Null()]),
  /** Blocks the call and message actions wherever this row is shown. A list
   *  that offers to ring a do-not-contact customer is a compliance bug. */
  doNotContact: Type22.Boolean(),
  leadStatus: enumOf(LEAD_STATUSES),
  leadRating: Type22.Union([enumOf(LEAD_RATINGS), Type22.Null()]),
  ownerId: Type22.Union([Type22.String({ format: "uuid" }), Type22.Null()]),
  ownerName: Type22.Union([Type22.String(), Type22.Null()]),
  reason: enumOf(ATTENTION_REASONS),
  /** The overdue follow-up, when that is the reason — so the row can offer to move it. */
  followUpId: Type22.Union([Type22.String({ format: "uuid" }), Type22.Null()]),
  followUpDueAt: Type22.Union([Type22.String({ format: "date-time" }), Type22.Null()]),
  lastActivityAt: Type22.Union([Type22.String({ format: "date-time" }), Type22.Null()]),
  createdAt: Type22.String({ format: "date-time" }),
  /** Days since the last activity, or since creation when there has never been one. */
  idleDays: Type22.Integer()
});
var AttentionGroup = Type22.Object({
  reason: enumOf(ATTENTION_REASONS),
  total: Type22.Integer(),
  items: Type22.Array(AttentionLead)
});
var NeedsAttentionQuery = Type22.Object({
  /** How many days without activity counts as stale. */
  staleDays: Type22.Optional(Type22.Integer({ minimum: 1, maximum: 365, default: ATTENTION_STALE_DAYS })),
  /** Narrow to the signed-in operator's own leads. */
  mineOnly: Type22.Optional(Type22.Boolean()),
  /** Narrow to one operator. Ignored when `mineOnly` is set. */
  ownerId: Type22.Optional(Type22.String({ format: "uuid" })),
  /** Rows returned across all groups. The totals are never capped. */
  limit: Type22.Optional(Type22.Integer({ minimum: 1, maximum: 200, default: 30 }))
});
var NeedsAttentionResponse = Type22.Object({
  generatedAt: Type22.String({ format: "date-time" }),
  /** Echoed back resolved, so the UI labels the window it actually got. */
  staleDays: Type22.Integer(),
  /** Every at-risk open lead, counted. This is the number that belongs in the nav. */
  total: Type22.Integer(),
  /** Always all three reasons, in urgency order, even when a group is empty. */
  groups: Type22.Array(AttentionGroup)
});

// src/notes.ts
import { Type as Type23 } from "@sinclair/typebox";
var NOTE_BODY_MAX = 4e3;
var NOTES_PAGE_MAX = 200;
var Note = Type23.Object({
  id: Type23.Integer(),
  contactId: Type23.String({ format: "uuid" }),
  /**
   * WHICH lead this note is about. A contact may hold several open leads at
   * once, so a note that names none is a note nobody can file — the operator
   * picks explicitly, and only a deliberately contact-level note is null.
   */
  leadId: Type23.Union([Type23.String({ format: "uuid" }), Type23.Null()]),
  authorName: Type23.Union([Type23.String(), Type23.Null()]),
  body: Type23.String(),
  createdAt: Type23.String({ format: "date-time" }),
  /** Equal to createdAt until someone edits it; the UI shows "edited" after. */
  updatedAt: Type23.String({ format: "date-time" })
});
var CreateNoteBody = Type23.Object({
  contactId: Type23.String({ format: "uuid" }),
  leadId: Type23.Optional(Type23.String({ format: "uuid" })),
  body: Type23.String({ minLength: 1, maxLength: NOTE_BODY_MAX })
});
var UpdateNoteBody = Type23.Object({
  body: Type23.Optional(Type23.String({ minLength: 1, maxLength: NOTE_BODY_MAX })),
  leadId: Type23.Optional(Type23.Union([Type23.String({ format: "uuid" }), Type23.Null()]))
});
var ListNotesQuery = Type23.Object({
  contactId: Type23.String({ format: "uuid" }),
  /** Narrows the list to one lead — the lead panel's own note list. */
  leadId: Type23.Optional(Type23.String({ format: "uuid" })),
  limit: Type23.Optional(Type23.Integer({ minimum: 1, maximum: NOTES_PAGE_MAX, default: 50 }))
});

// src/notifications.ts
import { Type as Type24 } from "@sinclair/typebox";
var NOTIFICATION_TYPES = [
  "follow_up_due",
  "follow_up_overdue",
  /** The first reminder went unread; five minutes left. */
  "follow_up_chase",
  /** One per operator per hour: what is coming in the next sixty minutes. */
  "follow_up_digest",
  "lead_assigned",
  "target_set",
  /** A customer is on the phone RIGHT NOW. The only type that is urgent. */
  "call_received"
];
var AUDIBLE_NOTIFICATION_TYPES = [
  "call_received",
  "follow_up_due",
  "follow_up_chase",
  "follow_up_digest"
];
var NOTIFICATION_TYPE_LABELS = {
  follow_up_due: "Follow-up due",
  follow_up_overdue: "Follow-up overdue",
  follow_up_chase: "Follow-up starting soon",
  follow_up_digest: "Your next hour",
  lead_assigned: "Lead assigned",
  target_set: "Target set",
  call_received: "Incoming call"
};
var Notification = Type24.Object({
  id: Type24.Integer(),
  notificationType: enumOf(NOTIFICATION_TYPES),
  title: Type24.String(),
  body: Type24.Union([Type24.String(), Type24.Null()]),
  /** Where clicking it goes. Both may be null for a notification about nothing in particular. */
  followUpId: Type24.Union([Type24.String({ format: "uuid" }), Type24.Null()]),
  contactId: Type24.Union([Type24.String({ format: "uuid" }), Type24.Null()]),
  /**
   * The number to ring, carried so the reminder itself can offer "copy" and
   * "call" without a second round trip.
   *
   * Joined live rather than denormalised, for the same reason as `leadName`
   * just below: a contact who corrects their number would otherwise be rung
   * on the old one by every reminder already in the bell.
   */
  contactPhone: Type24.Union([Type24.String(), Type24.Null()]),
  /** The lead the follow-up chases, resolved server-side so the panel can name it. */
  leadId: Type24.Union([Type24.String({ format: "uuid" }), Type24.Null()]),
  leadName: Type24.Union([Type24.String(), Type24.Null()]),
  readAt: Type24.Union([Type24.String({ format: "date-time" }), Type24.Null()]),
  createdAt: Type24.String({ format: "date-time" })
});
var NotificationPage = pageOf(Notification);
var ListNotificationsQuery = Type24.Composite([
  PageQuery,
  Type24.Object({ unreadOnly: Type24.Optional(Type24.Boolean()) })
]);
var NOTIFICATION_PANEL_LIMIT = 20;
var UnreadCount = Type24.Object({ unread: Type24.Integer() });
var MarkReadResponse = Type24.Object({ updated: Type24.Integer() });

// src/paste.ts
var NAME_MAX_CHARS = 60;
var EXPORT_LINE = /^\[?\d{1,2}[/.]\d{1,2}[/.]\d{2,4},?\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:[ap]\.?m\.?)?\]?\s*-?\s*/i;
var SPEAKER = /^([^:]{1,60}):\s*/;
var MESSAGE_WORDS = /* @__PURE__ */ new Set([
  "a",
  "about",
  "am",
  "and",
  "any",
  "anyone",
  "are",
  "ask",
  "asked",
  "at",
  "available",
  "can",
  "cost",
  "could",
  "delivery",
  "deliver",
  "do",
  "does",
  "for",
  "from",
  "get",
  "give",
  "hello",
  "hey",
  "hi",
  "how",
  "i",
  "in",
  "interested",
  "is",
  "it",
  "know",
  "me",
  "my",
  "need",
  "needs",
  "of",
  "on",
  "order",
  "please",
  "pls",
  "price",
  "quote",
  "send",
  "sir",
  "thanks",
  "the",
  "this",
  "to",
  "want",
  "wants",
  "we",
  "what",
  "when",
  "where",
  "will",
  "with",
  "would",
  "you",
  "your",
  /* Tamil script. Hello, what, how much, price, want, need, is there, send,
     tell, thanks, order, delivery — the words an enquiry opens with. */
  "\u0BB5\u0BA3\u0B95\u0BCD\u0B95\u0BAE\u0BCD",
  "\u0B8E\u0BA9\u0BCD\u0BA9",
  "\u0B8E\u0BB5\u0BCD\u0BB5\u0BB3\u0BB5\u0BC1",
  "\u0BB5\u0BBF\u0BB2\u0BC8",
  "\u0BB5\u0BC7\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD",
  "\u0BB5\u0BC7\u0BA3\u0BC1\u0BAE\u0BCD",
  "\u0BA4\u0BC7\u0BB5\u0BC8",
  "\u0B87\u0BB0\u0BC1\u0B95\u0BCD\u0B95\u0BBE",
  "\u0B87\u0BB0\u0BC1\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BA4\u0BBE",
  "\u0B87\u0BB0\u0BC1\u0B95\u0BCD\u0B95\u0BC1\u0BAE\u0BBE",
  "\u0B85\u0BA9\u0BC1\u0BAA\u0BCD\u0BAA\u0BC1\u0B99\u0BCD\u0B95",
  "\u0B9A\u0BCA\u0BB2\u0BCD\u0BB2\u0BC1\u0B99\u0BCD\u0B95",
  "\u0BA8\u0BA9\u0BCD\u0BB1\u0BBF",
  "\u0B86\u0BB0\u0BCD\u0B9F\u0BB0\u0BCD",
  "\u0B9F\u0BC6\u0BB2\u0BBF\u0BB5\u0BB0\u0BBF",
  "\u0BB0\u0BC7\u0B9F\u0BCD",
  /* Tanglish — the same words typed in Latin script, which is how most of
     them actually arrive. Spellings vary, so the common variants are listed
     rather than guessed at with a stemmer. */
  "vanakkam",
  "enna",
  "ennaku",
  "enaku",
  "evlo",
  "evvalavu",
  "evlovu",
  "vilai",
  "venum",
  "vendum",
  "theva",
  "irukka",
  "irukku",
  "irukkuma",
  "anupunga",
  "anuppunga",
  "sollunga",
  "nandri",
  "rate",
  "stock",
  "pannunga",
  "kudunga",
  "vaangalam",
  "seri"
]);
var NAME_MAX_WORDS = 5;
var looksLikeName = (line) => {
  if (line.length === 0 || line.length > NAME_MAX_CHARS) return false;
  if (!/\p{L}/u.test(line)) return false;
  if (line.replace(/\D/g, "").length * 2 >= line.length) return false;
  const words = line.split(/\s+/).filter(Boolean);
  if (words.length > NAME_MAX_WORDS) return false;
  return !words.some((w) => MESSAGE_WORDS.has(w.toLowerCase().replace(/[^\p{L}\p{M}]/gu, "")));
};
function parseWhatsappPaste(text) {
  const rawLines = text.split(/\r?\n/).map((l) => l.trim());
  let exportName = null;
  const lines = rawLines.map((line) => {
    if (!EXPORT_LINE.test(line)) return line;
    const body = line.replace(EXPORT_LINE, "");
    const speaker = body.match(SPEAKER);
    if (speaker && looksLikeName(speaker[1].trim())) {
      exportName ??= speaker[1].trim();
      return body.slice(speaker[0].length).trim();
    }
    return body.trim();
  });
  let phoneE164 = null;
  let phoneLine = -1;
  for (let i = 0; i < lines.length && !phoneE164; i++) {
    const candidate = lines[i].match(/\+?[\d][\d\s()./-]{8,}\d/);
    if (!candidate) continue;
    const parsed = normalisePhone(candidate[0]);
    if (parsed) {
      phoneE164 = parsed;
      if (candidate[0].trim().length >= lines[i].length - 2) phoneLine = i;
    }
  }
  let name = exportName;
  let nameLine = -1;
  if (!name) {
    const first = lines.findIndex((l) => l !== "");
    if (first !== -1 && first !== phoneLine && looksLikeName(lines[first])) {
      name = lines[first];
      nameLine = first;
    }
  }
  const note = lines.filter((_, i) => i !== phoneLine && i !== nameLine).join("\n").trim();
  return { phoneE164, name, note };
}

// src/requirements.ts
import { Type as Type25 } from "@sinclair/typebox";
var REQUIREMENT_KINDS = [
  "feature_request",
  "enhancement",
  "bug",
  "integration",
  "objection",
  "other"
];
var REQUIREMENT_LIFECYCLES = ["backlog", "planned", "in_progress", "shipped", "declined"];
var REQUIREMENT_ASKS = ["blocker", "wanted", "nice_to_have"];
var REQUIREMENT_KIND_LABELS = {
  feature_request: "Feature request",
  enhancement: "Enhancement",
  bug: "Bug",
  integration: "Integration",
  objection: "Objection",
  other: "Other"
};
var REQUIREMENT_KIND_HINTS = {
  feature_request: "A capability the product does not have yet",
  enhancement: "Something that exists but needs to work better",
  bug: "Something is broken",
  integration: "Needs to connect to another service",
  objection: "A reason not to buy, including price",
  other: "None of the above"
};
var REQUIREMENT_LIFECYCLE_LABELS = {
  backlog: "Backlog",
  planned: "Planned",
  in_progress: "In progress",
  shipped: "Shipped",
  declined: "Declined"
};
var REQUIREMENT_ASK_LABELS = {
  blocker: "Blocker",
  wanted: "Wanted",
  nice_to_have: "Nice to have"
};
var REQUIREMENT_NAME_MAX = 200;
var REQUIREMENT_DESCRIPTION_MAX = 2e3;
var REQUIREMENT_DETAIL_MAX = 2e3;
var isRequirementShipped = (lifecycle) => lifecycle === "shipped";
var RequirementCounts = Type25.Object({
  /** Distinct OPEN leads with this requirement still open against them. */
  openLeads: Type25.Integer(),
  /** Of those, the ones marked `blocker`. */
  blockers: Type25.Integer(),
  /** Every lead that ever raised it, open or closed — the call-back list size. */
  totalLeads: Type25.Integer(),
  /** Distinct people behind `totalLeads`; one seller may raise it on two leads. */
  totalContacts: Type25.Integer()
});
var RequirementCatalogEntry = Type25.Object({
  id: Type25.String({ format: "uuid" }),
  kind: enumOf(REQUIREMENT_KINDS),
  name: Type25.String(),
  description: Type25.Union([Type25.String(), Type25.Null()]),
  lifecycle: enumOf(REQUIREMENT_LIFECYCLES),
  shippedAt: Type25.Union([Type25.String({ format: "date-time" }), Type25.Null()]),
  createdAt: Type25.String({ format: "date-time" }),
  updatedAt: Type25.String({ format: "date-time" }),
  /**
   * Present only when the caller asked for `withCounts`. Null rather than
   * zeroes, so "nobody wants this" and "we did not count" cannot be confused —
   * a zero the UI trusted would quietly demote a feature twelve leads need.
   */
  counts: Type25.Union([RequirementCounts, Type25.Null()])
});
var REQUIREMENT_SORTS = ["demand_desc", "blockers_desc", "name_asc", "created_desc", "lifecycle_asc"];
var REQUIREMENT_SORT_LABELS = {
  demand_desc: "Most requested",
  blockers_desc: "Most blocking",
  name_asc: "By name",
  created_desc: "Newest first",
  lifecycle_asc: "By stage"
};
var ListRequirementCatalogQuery = Type25.Composite([
  PageQuery,
  Type25.Object({
    kind: Type25.Optional(enumOf(REQUIREMENT_KINDS)),
    lifecycle: Type25.Optional(enumOf(REQUIREMENT_LIFECYCLES)),
    search: Type25.Optional(Type25.String({ maxLength: 120 })),
    /**
     * Counting is a join over every lead requirement, so it is opt-in: the
     * inline picker on a lead wants the names and nothing else.
     */
    withCounts: Type25.Optional(Type25.Boolean()),
    sort: Type25.Optional(enumOf(REQUIREMENT_SORTS))
  })
]);
var RequirementCatalogPage = pageOf(RequirementCatalogEntry);
var CreateRequirementBody = Type25.Object({
  kind: enumOf(REQUIREMENT_KINDS),
  name: Type25.String({ minLength: 1, maxLength: REQUIREMENT_NAME_MAX }),
  description: Type25.Optional(Type25.Union([Type25.String({ maxLength: REQUIREMENT_DESCRIPTION_MAX }), Type25.Null()])),
  lifecycle: Type25.Optional(enumOf(REQUIREMENT_LIFECYCLES))
});
var UpdateRequirementBody = Type25.Object({
  kind: Type25.Optional(enumOf(REQUIREMENT_KINDS)),
  name: Type25.Optional(Type25.String({ minLength: 1, maxLength: REQUIREMENT_NAME_MAX })),
  description: Type25.Optional(Type25.Union([Type25.String({ maxLength: REQUIREMENT_DESCRIPTION_MAX }), Type25.Null()])),
  lifecycle: Type25.Optional(enumOf(REQUIREMENT_LIFECYCLES))
});
var LeadRequirement = Type25.Object({
  /** The `lead_requirements` row — the thing DELETE and PATCH address. */
  id: Type25.String({ format: "uuid" }),
  leadId: Type25.String({ format: "uuid" }),
  leadName: Type25.String(),
  leadStatus: enumOf(LEAD_STATUSES),
  contactId: Type25.String({ format: "uuid" }),
  catalogId: Type25.String({ format: "uuid" }),
  kind: enumOf(REQUIREMENT_KINDS),
  name: Type25.String(),
  lifecycle: enumOf(REQUIREMENT_LIFECYCLES),
  shippedAt: Type25.Union([Type25.String({ format: "date-time" }), Type25.Null()]),
  /** What THIS prospect said, in their words. The catalogue holds the label. */
  detail: Type25.Union([Type25.String(), Type25.Null()]),
  ask: enumOf(REQUIREMENT_ASKS),
  isOpen: Type25.Boolean(),
  raisedById: Type25.Union([Type25.String({ format: "uuid" }), Type25.Null()]),
  raisedByName: Type25.Union([Type25.String(), Type25.Null()]),
  createdAt: Type25.String({ format: "date-time" }),
  updatedAt: Type25.String({ format: "date-time" })
});
var AttachFields = Type25.Object({
  detail: Type25.Optional(Type25.Union([Type25.String({ maxLength: REQUIREMENT_DETAIL_MAX }), Type25.Null()])),
  ask: Type25.Optional(enumOf(REQUIREMENT_ASKS))
});
var AttachRequirementBody = Type25.Union([
  Type25.Composite([AttachFields, Type25.Object({ catalogId: Type25.String({ format: "uuid" }) })]),
  Type25.Composite([
    AttachFields,
    Type25.Object({
      kind: enumOf(REQUIREMENT_KINDS),
      name: Type25.String({ minLength: 1, maxLength: REQUIREMENT_NAME_MAX }),
      description: Type25.Optional(Type25.Union([Type25.String({ maxLength: REQUIREMENT_DESCRIPTION_MAX }), Type25.Null()]))
    })
  ])
]);
var UpdateLeadRequirementBody = Type25.Object({
  detail: Type25.Optional(Type25.Union([Type25.String({ maxLength: REQUIREMENT_DETAIL_MAX }), Type25.Null()])),
  ask: Type25.Optional(enumOf(REQUIREMENT_ASKS)),
  /** Cleared when the prospect withdraws the ask or it is delivered to them. */
  isOpen: Type25.Optional(Type25.Boolean())
});
var RequirementLeadRef = Type25.Object({
  /** The `lead_requirements` row. */
  id: Type25.String({ format: "uuid" }),
  leadId: Type25.String({ format: "uuid" }),
  leadName: Type25.String(),
  leadStatus: enumOf(LEAD_STATUSES),
  contactId: Type25.String({ format: "uuid" }),
  contactName: Type25.Union([Type25.String(), Type25.Null()]),
  /** Null when the customer has no number — see contacts.phoneE164. */
  contactPhone: Type25.Union([Type25.String(), Type25.Null()]),
  ownerName: Type25.Union([Type25.String(), Type25.Null()]),
  detail: Type25.Union([Type25.String(), Type25.Null()]),
  ask: enumOf(REQUIREMENT_ASKS),
  isOpen: Type25.Boolean(),
  raisedAt: Type25.String({ format: "date-time" })
});
var ListRequirementLeadsQuery = Type25.Composite([
  PageQuery,
  Type25.Object({
    /** Default true: the call-back list is the people still waiting. */
    openOnly: Type25.Optional(Type25.Boolean())
  })
]);
var RequirementLeadPage = pageOf(RequirementLeadRef);
var DuplicateRequirementResponse = Type25.Object({
  error: Type25.Literal("conflict"),
  message: Type25.String(),
  existingRequirementId: Type25.String({ format: "uuid" })
});
var AttachManyBody = Type25.Object({
  asks: Type25.Array(
    Type25.Object({
      leadId: Type25.String({ format: "uuid" }),
      detail: Type25.Optional(Type25.Union([Type25.String({ maxLength: REQUIREMENT_DETAIL_MAX }), Type25.Null()])),
      ask: Type25.Optional(enumOf(REQUIREMENT_ASKS))
    }),
    { minItems: 1, maxItems: 50 }
  )
});
var AttachManyResponse = Type25.Object({
  /** Newly linked. */
  added: Type25.Integer(),
  /** Already linked, so left alone — not an error, and worth reporting. */
  alreadyPresent: Type25.Integer(),
  requirements: Type25.Array(LeadRequirement)
});

// src/settings.ts
import { Type as Type26 } from "@sinclair/typebox";
var INTEGRATION_PROVIDERS = ["frejun"];
var FREJUN_EVENTS = [
  "call.status",
  "call.recording",
  "call.summary",
  "call.insights",
  "call.propertyChange.notes",
  "call.propertyChange.call_reason",
  "call.propertyChange.call_outcome",
  "call.propertyChange.metadata"
];
var FREJUN_EVENT_LABELS = {
  "call.status": "Call status changes",
  "call.recording": "Recording ready",
  "call.summary": "Call summary",
  "call.insights": "Transcript and AI insights",
  "call.propertyChange.notes": "Call note edited",
  "call.propertyChange.call_reason": "Call reason changed",
  "call.propertyChange.call_outcome": "Call outcome changed",
  "call.propertyChange.metadata": "Metadata changed"
};
var IntegrationSettings = Type26.Object({
  provider: enumOf(INTEGRATION_PROVIDERS),
  isEnabled: Type26.Boolean(),
  clientId: Type26.Union([Type26.String(), Type26.Null()]),
  hasClientSecret: Type26.Boolean(),
  hasAccessToken: Type26.Boolean(),
  /** Last four characters only, so an operator can tell which key is in place. */
  secretHint: Type26.Union([Type26.String(), Type26.Null()]),
  virtualNumber: Type26.Union([Type26.String(), Type26.Null()]),
  /**
   * The FreJun user calls are placed as.
   *
   * FreJun identifies the caller by `user_email` — their own login — which is
   * NOT necessarily the email somebody signs into this CRM with. Assuming they
   * match is why a correctly configured account answered "Active FreJun user
   * doesnt exist with the email".
   *
   * One address for the tenant, like the virtual number, because `app_users`
   * has no column for a per-operator FreJun identity and the schema is frozen
   * — the same limitation docs/03 records for the agent number. The cost is
   * that FreJun attributes every call to this one user; the CRM's own records
   * still name whoever actually dialled.
   */
  userEmail: Type26.Union([Type26.String(), Type26.Null()]),
  baseUrl: Type26.Union([Type26.String(), Type26.Null()]),
  /**
   * Who owns a lead created from an incoming call.
   *
   * WHY THIS IS A SETTING AND NOT A DEPLOY VARIABLE. It was
   * `FREJUN_INGEST_OWNER_ID`, which meant that the day the person working the
   * queue changed, every new lead kept landing on the old account — and
   * fixing it needed a server restart by whoever had shell access, not by
   * the admin who knew it was wrong. It is also one id for the whole
   * deployment, where the rest of this row is already per-organisation.
   *
   * The variable is kept as the fallback, so an installation that never
   * opens this screen behaves exactly as it did.
   *
   * NULL means nobody owns it. That is a real choice rather than an absence:
   * an unowned lead is visible to every operator, where one owned by a
   * colleague is not — so "unassigned" is the right answer for a team that
   * triages together.
   */
  ingestOwnerId: Type26.Union([Type26.String({ format: "uuid" }), Type26.Null()]),
  /** The name behind `ingestOwnerId`, so the screen need not look it up. */
  ingestOwnerName: Type26.Union([Type26.String(), Type26.Null()]),
  /**
   * True when `ingestOwnerId` is coming from the environment variable rather
   * than from this row — so the screen can say where the value it is showing
   * actually comes from.
   */
  ingestOwnerFromEnv: Type26.Boolean(),
  /** The URL FreJun must call. Generated, not typed — see the settings page. */
  callbackUrl: Type26.Union([Type26.String(), Type26.Null()]),
  hasWebhookSecret: Type26.Boolean(),
  registeredWebhooks: Type26.Array(Type26.Object({ event: Type26.String(), id: Type26.Union([Type26.String(), Type26.Null()]) })),
  webhooksRegisteredAt: Type26.Union([Type26.String({ format: "date-time" }), Type26.Null()]),
  lastCheckedAt: Type26.Union([Type26.String({ format: "date-time" }), Type26.Null()]),
  lastCheckOk: Type26.Union([Type26.Boolean(), Type26.Null()]),
  lastCheckMessage: Type26.Union([Type26.String(), Type26.Null()]),
  updatedAt: Type26.Union([Type26.String({ format: "date-time" }), Type26.Null()])
});
var UpdateIntegrationBody = Type26.Partial(
  Type26.Object({
    isEnabled: Type26.Boolean(),
    clientId: Type26.Union([Type26.String({ maxLength: 200 }), Type26.Null()]),
    clientSecret: Type26.Union([Type26.String({ maxLength: 400 }), Type26.Null()]),
    accessToken: Type26.Union([Type26.String({ maxLength: 4e3 }), Type26.Null()]),
    virtualNumber: Type26.Union([Type26.String({ maxLength: 20 }), Type26.Null()]),
    /** The FreJun login calls are placed as — see IntegrationSettings.userEmail. */
    userEmail: Type26.Union([Type26.String({ format: "email", maxLength: 200 }), Type26.Null()]),
    baseUrl: Type26.Union([Type26.String({ maxLength: 300 }), Type26.Null()]),
    /**
     * Who owns a lead that arrives from a call — see `IntegrationSettings`.
     *
     * Null means nobody: the lead is created unowned, which every operator
     * can still see, and whoever picks it up claims it.
     */
    ingestOwnerId: Type26.Union([Type26.String({ format: "uuid" }), Type26.Null()])
  })
);
var WebhookRegistration = Type26.Object({
  event: Type26.String(),
  ok: Type26.Boolean(),
  id: Type26.Union([Type26.String(), Type26.Null()]),
  message: Type26.Union([Type26.String(), Type26.Null()])
});
var SaveIntegrationResponse = Type26.Object({
  settings: IntegrationSettings,
  registered: Type26.Array(WebhookRegistration),
  /** Regenerated whenever the hooks are, and shown once. */
  webhookSecret: Type26.Union([Type26.String(), Type26.Null()]),
  registrationSkipped: Type26.Union([Type26.String(), Type26.Null()])
});
var CalendarLink = Type26.Object({
  endpointUrl: Type26.String(),
  isEnabled: Type26.Boolean(),
  lastSyncAt: Type26.Union([Type26.String({ format: "date-time" }), Type26.Null()]),
  lastSyncOk: Type26.Union([Type26.Boolean(), Type26.Null()]),
  lastSyncMessage: Type26.Union([Type26.String(), Type26.Null()])
});
var CalendarStatus = Type26.Object({
  /** Null when nothing is connected. */
  link: Type26.Union([CalendarLink, Type26.Null()])
});
var ConnectCalendarBody = Type26.Object({
  /** The deployed Apps Script web app URL. Must be https. */
  endpointUrl: Type26.String({ minLength: 1, maxLength: 500, pattern: "^https://" })
});
var ConnectCalendarResponse = Type26.Object({
  link: CalendarLink,
  /** Shown once. Paste it into the script; it is never returned again. */
  secret: Type26.String()
});

// src/tags.ts
import { Type as Type27 } from "@sinclair/typebox";
var TAG_NAME_MAX = 60;
var TAG_COLOR_PATTERN = "^#[0-9a-fA-F]{6}$";
var Tag = Type27.Object({
  id: Type27.String({ format: "uuid" }),
  name: Type27.String(),
  color: Type27.Union([Type27.String(), Type27.Null()])
});
var LeadTag = Type27.Object({
  /** The taggables row — the thing DELETE removes. */
  id: Type27.String({ format: "uuid" }),
  tagId: Type27.String({ format: "uuid" }),
  leadId: Type27.String({ format: "uuid" }),
  name: Type27.String(),
  color: Type27.Union([Type27.String(), Type27.Null()]),
  /** §9 logs "Tag added" as a timed event, so the chip can say when. */
  addedAt: Type27.String({ format: "date-time" }),
  addedByName: Type27.Union([Type27.String(), Type27.Null()])
});
var TagColor = Type27.Union([Type27.String({ pattern: TAG_COLOR_PATTERN }), Type27.Null()]);
var BULK_TAG_MAX = 100;
var CreateTagBody = Type27.Object({
  name: Type27.String({ minLength: 1, maxLength: TAG_NAME_MAX }),
  color: Type27.Optional(TagColor)
});
var AddLeadTagBody = Type27.Union([
  Type27.Object({ tagId: Type27.String({ format: "uuid" }) }),
  Type27.Object({
    name: Type27.String({ minLength: 1, maxLength: TAG_NAME_MAX }),
    color: Type27.Optional(TagColor)
  })
]);
var ListTagsQuery = Type27.Object({
  search: Type27.Optional(Type27.String({ maxLength: 120 })),
  limit: Type27.Optional(Type27.Integer({ minimum: 1, maximum: 200, default: 100 }))
});
var DuplicateTagResponse = Type27.Object({
  error: Type27.Literal("conflict"),
  message: Type27.String(),
  existingTagId: Type27.String({ format: "uuid" })
});
var BulkTagLeadsBody = Type27.Intersect([
  Type27.Object({
    leadIds: Type27.Array(Type27.String({ format: "uuid" }), { minItems: 1, maxItems: BULK_TAG_MAX })
  }),
  AddLeadTagBody
]);
var BulkTagLeadsResponse = Type27.Object({
  /**
   * The VOCABULARY entry, not an attachment.
   *
   * A `LeadTag` describes one word on one lead — it carries that lead's id and
   * who attached it — and a batch has no single one of those to report. The UI
   * only needs to name the word it just applied.
   */
  tag: Tag,
  /** How many leads gained the tag. */
  tagged: Type27.Integer(),
  /** Already carried it, so nothing was written. */
  skipped: Type27.Integer()
});

// src/templates.ts
import { Type as Type28 } from "@sinclair/typebox";
var TEMPLATE_NAME_MAX = 120;
var WHATSAPP_BODY_MAX = 1024;
var WA_LINK_WARN_LENGTH = 1500;
var WA_LINK_MAX_LENGTH = 2048;
var TEMPLATE_VARIABLES = [
  // The customer
  "Name",
  "FirstName",
  "Phone",
  "Email",
  "Company",
  "Designation",
  // The lead
  "BusinessName",
  "WhichBusiness",
  "Lead",
  "Source",
  /**
   * The old spellings, kept so templates saved before the field renames still
   * validate and still render. Each is hidden from the editor's chip list and
   * resolves from the same source as its replacement, so nothing new picks one
   * up and nothing already saved breaks:
   *
   *   {Enquiry}  → {Lead}
   *   {Product}  → {BusinessName}   (the field is "Business name" now)
   *   {Business} → {WhichBusiness}  (the field is "Which business" now)
   *
   * `{Product}` and `{Business}` are the worst kind of stale name: they still
   * resolved, so nothing failed, but `{Product}` filled in a business name and
   * `{Business}` filled in which branch — an operator reading the chip list
   * would reasonably have used them the other way round.
   */
  "Enquiry",
  "Product",
  "Business",
  // Their subscription (§17/§18)
  "AccountStatus",
  "ExpiryDate",
  "DaysToExpiry",
  // Us
  "AgentName",
  "Brand",
  "Today"
];
var TEMPLATE_VARIABLE_LABELS = {
  Name: "Customer name",
  FirstName: "First name only",
  Phone: "Their phone number",
  Email: "Their email",
  Company: "Their company",
  Designation: "Their role",
  BusinessName: "Business name on the lead",
  WhichBusiness: "Which of their businesses",
  Lead: "The lead title",
  Enquiry: "The lead title (old name for {Lead})",
  Product: "Business name on the lead (old name for {BusinessName})",
  Business: "Which of their businesses (old name for {WhichBusiness})",
  Source: "Where the lead came from",
  AccountStatus: "Subscription state",
  ExpiryDate: "Subscription end date",
  DaysToExpiry: "Days until it expires",
  AgentName: "You, the sender",
  Brand: "Your organisation",
  Today: "Today's date"
};
var TEMPLATE_VARIABLE_REPLACEMENTS = {
  Enquiry: "Lead",
  Product: "BusinessName",
  Business: "WhichBusiness"
};
var DEPRECATED_TEMPLATE_VARIABLES = Object.keys(TEMPLATE_VARIABLE_REPLACEMENTS);
var isDeprecatedTemplateVariable = (v) => Object.hasOwn(TEMPLATE_VARIABLE_REPLACEMENTS, v);
var CURRENT_TEMPLATE_VARIABLES = TEMPLATE_VARIABLES.filter((v) => !isDeprecatedTemplateVariable(v));
function withDeprecatedAliases(values) {
  const out = { ...values };
  for (const [old, now] of Object.entries(TEMPLATE_VARIABLE_REPLACEMENTS)) {
    const value = out[now];
    if (value !== void 0) out[old] = value;
  }
  return out;
}
var TEMPLATE_VARIABLE_GROUPS = [
  { label: "Customer", variables: ["Name", "FirstName", "Phone", "Email", "Company", "Designation"] },
  { label: "Lead", variables: ["BusinessName", "WhichBusiness", "Lead", "Source"] },
  { label: "Subscription", variables: ["AccountStatus", "ExpiryDate", "DaysToExpiry"] },
  { label: "Sender", variables: ["AgentName", "Brand", "Today"] }
];
var PLACEHOLDER_RE = /\{([A-Za-z0-9_]+)\}/g;
function parseTemplateVariables(body) {
  const seen = [];
  for (const match of body.matchAll(PLACEHOLDER_RE)) {
    const name = match[1];
    if (!seen.includes(name)) seen.push(name);
  }
  return seen;
}
function unknownTemplateVariables(body) {
  return parseTemplateVariables(body).filter((v) => !TEMPLATE_VARIABLES.includes(v));
}
function renderTemplate(body, values) {
  return body.replace(PLACEHOLDER_RE, (whole, name) => {
    const value = values[name];
    return value === void 0 || value === null ? whole : value;
  });
}
function unresolvedPlaceholders(rendered) {
  return parseTemplateVariables(rendered);
}
function buildWaLink(phoneE164, body) {
  return `https://wa.me/${phoneE164.replace(/\D/g, "")}?text=${encodeURIComponent(body)}`;
}
function waLinkLength(phoneE164, body) {
  return buildWaLink(phoneE164, body).length;
}
var TEMPLATE_APPROVAL_STATUSES = ["draft", "pending", "approved", "rejected"];
var WHATSAPP_MESSAGE_TYPES = ["template", "direct"];
var WHATSAPP_SEND_METHODS = ["click_to_chat", "cloud_api", "import"];
var WHATSAPP_STATUSES = ["link_opened", "queued", "sent", "delivered", "read", "failed"];
var WHATSAPP_STATUS_LABELS = {
  link_opened: "Opened in WhatsApp",
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed"
};
var WHATSAPP_MESSAGE_TYPE_LABELS = {
  template: "Template message",
  direct: "Direct message"
};
var WhatsappTemplate = Type28.Object({
  id: Type28.String({ format: "uuid" }),
  name: Type28.String(),
  body: Type28.String(),
  /** Parsed from the body on write — never supplied by the client. */
  variables: Type28.Array(Type28.String()),
  /** Phase 2 only; click-to-chat needs no Meta approval, so it stays `draft`. */
  approvalStatus: enumOf(TEMPLATE_APPROVAL_STATUSES),
  createdAt: Type28.String({ format: "date-time" }),
  updatedAt: Type28.String({ format: "date-time" })
});
var TemplatePage = pageOf(WhatsappTemplate);
var ListTemplatesQuery = Type28.Composite([
  PageQuery,
  Type28.Object({
    /** §6 "Search templates" — matches the name and the body. */
    search: Type28.Optional(Type28.String({ maxLength: 120 }))
  })
]);
var CreateTemplateBody = Type28.Object({
  name: Type28.String({ minLength: 1, maxLength: TEMPLATE_NAME_MAX }),
  body: Type28.String({ minLength: 1, maxLength: WHATSAPP_BODY_MAX })
});
var UpdateTemplateBody = Type28.Object({
  name: Type28.Optional(Type28.String({ minLength: 1, maxLength: TEMPLATE_NAME_MAX })),
  body: Type28.Optional(Type28.String({ minLength: 1, maxLength: WHATSAPP_BODY_MAX }))
});
var DuplicateTemplateResponse = Type28.Object({
  error: Type28.Literal("conflict"),
  message: Type28.String(),
  existingTemplateId: Type28.String({ format: "uuid" })
});
var WhatsappMessage = Type28.Object({
  id: Type28.Integer(),
  contactId: Type28.String({ format: "uuid" }),
  /** WHICH enquiry this message was about. Null is a deliberate person-level send. */
  leadId: Type28.Union([Type28.String({ format: "uuid" }), Type28.Null()]),
  messageType: enumOf(WHATSAPP_MESSAGE_TYPES),
  sendMethod: enumOf(WHATSAPP_SEND_METHODS),
  templateId: Type28.Union([Type28.String({ format: "uuid" }), Type28.Null()]),
  /** Copied at send time, so the log survives a rename or a delete. */
  templateNameSnapshot: Type28.Union([Type28.String(), Type28.Null()]),
  /** After substitution — exactly what was put in front of the customer. */
  renderedBody: Type28.String(),
  /** Always `link_opened` in phase 1. See the note at the top of this file. */
  status: enumOf(WHATSAPP_STATUSES),
  sentAt: Type28.String({ format: "date-time" })
});
var SendWhatsappBody = Type28.Union([
  Type28.Object({
    messageType: Type28.Literal("template"),
    contactId: Type28.String({ format: "uuid" }),
    leadId: Type28.Optional(Type28.Union([Type28.String({ format: "uuid" }), Type28.Null()])),
    templateId: Type28.String({ format: "uuid" }),
    renderedBody: Type28.String({ minLength: 1, maxLength: WHATSAPP_BODY_MAX })
  }),
  Type28.Object({
    messageType: Type28.Literal("direct"),
    contactId: Type28.String({ format: "uuid" }),
    leadId: Type28.Optional(Type28.Union([Type28.String({ format: "uuid" }), Type28.Null()])),
    renderedBody: Type28.String({ minLength: 1, maxLength: WHATSAPP_BODY_MAX })
  })
]);

// src/today.ts
import { Type as Type29 } from "@sinclair/typebox";
var TODAY_SCOPES = ["mine", "team"];
var TODAY_JOB_KINDS = [
  // Now — what is already wrong.
  "call_review",
  "unanswered_message",
  "overdue",
  "callback",
  // The book — what is planned.
  "booked",
  // Inbox — what nobody has decided about.
  "unowned",
  "no_next_step",
  "stale"
];
var TodayJob = Type29.Object({
  /** Stable across refetches, and unique across the three blocks. */
  key: Type29.String(),
  kind: enumOf(TODAY_JOB_KINDS),
  /** The real reason, in the operator's words. Never a generic label. */
  why: Type29.String(),
  contactId: Type29.String({ format: "uuid" }),
  contactName: Type29.Union([Type29.String(), Type29.Null()]),
  contactPhone: Type29.Union([Type29.String(), Type29.Null()]),
  /** A list that offers to ring a do-not-contact customer is a compliance bug. */
  doNotContact: Type29.Boolean(),
  leadId: Type29.Union([Type29.String({ format: "uuid" }), Type29.Null()]),
  leadName: Type29.Union([Type29.String(), Type29.Null()]),
  /** Who owns it, so the row can say — and so Claim knows to appear. */
  ownerId: Type29.Union([Type29.String({ format: "uuid" }), Type29.Null()]),
  ownerName: Type29.Union([Type29.String(), Type29.Null()]),
  /** Set only on rows that came from a follow-up: what Done and Move act on. */
  followUpId: Type29.Union([Type29.String({ format: "uuid" }), Type29.Null()]),
  /** Set only on a callback row: what Listen acts on. */
  callId: Type29.Union([Type29.Integer(), Type29.Null()]),
  recordingUrl: Type29.Union([Type29.String(), Type29.Null()]),
  dueAt: Type29.Union([Type29.String({ format: "date-time" }), Type29.Null()]),
  /** How late, in whole minutes. Null when nothing was promised. */
  lateMinutes: Type29.Union([Type29.Integer(), Type29.Null()]),
  /** Days since anything happened. Null where it is not the point. */
  idleDays: Type29.Union([Type29.Integer(), Type29.Null()]),
  note: Type29.Union([Type29.String(), Type29.Null()])
});
var TodayHour = Type29.Object({
  /** 0–23 in the app timezone, or null for the trailing "After hours" group. */
  hour: Type29.Union([Type29.Integer({ minimum: 0, maximum: 23 }), Type29.Null()]),
  label: Type29.String(),
  /** How many calls this hour has historically carried. */
  calls: Type29.Integer(),
  /** 0–1, or null when too few calls to mean anything. */
  answerRate: Type29.Union([Type29.Number(), Type29.Null()]),
  best: Type29.Boolean(),
  /**
   * Below `TODAY_POOR_ANSWER_RATE`, with enough calls to say so. The UI
   * offers to move a booking out of one.
   */
  poor: Type29.Boolean(),
  items: Type29.Array(TodayJob)
});
var TodayBlock = Type29.Object({
  /** Everything that matched, which is not what `items` is capped to. */
  total: Type29.Integer(),
  items: Type29.Array(TodayJob)
});
var TODAY_BLOCK_CAP = 50;
var TODAY_BLOCK_MAX = 200;
var TODAY_POOR_ANSWER_RATE = 0.4;
var TODAY_WORKING_HOURS = { from: 9, to: 20 };
var TodayQuery = Type29.Object({
  /**
   * `mine` means MINE OR NOBODY'S. Work nobody has claimed belongs on the
   * queue of whoever is looking — leaving it off is how it ends up on no
   * list at all — and belongs on nobody's report of their own numbers.
   */
  scope: Type29.Optional(enumOf(TODAY_SCOPES)),
  limit: Type29.Optional(Type29.Integer({ minimum: 1, maximum: TODAY_BLOCK_MAX, default: TODAY_BLOCK_CAP }))
});
var TodayResponse = Type29.Object({
  scope: enumOf(TODAY_SCOPES),
  /** One instant for the whole page. The three blocks cannot disagree. */
  generatedAt: Type29.String({ format: "date-time" }),
  /** What is already wrong. Worked first, top to bottom. */
  now: TodayBlock,
  /** What is planned, by hour. */
  book: Type29.Object({ total: Type29.Integer(), hours: Type29.Array(TodayHour) }),
  /** What nobody has decided about. */
  inbox: TodayBlock,
  /** The hour most likely to be answered, for the "move it" prompt. */
  bestHour: Type29.Union([Type29.Integer({ minimum: 0, maximum: 23 }), Type29.Null()])
});

// src/welcome.ts
import { Type as Type30 } from "@sinclair/typebox";
var WelcomeWorkload = Type30.Object({
  /** Scheduled, the moment passed, nobody closed it. The most urgent thing here. */
  overdueFollowUps: Type30.Integer(),
  /** Due later today. Not a problem yet — this is the shape of the day. */
  dueToday: Type30.Integer(),
  /** Open leads with nothing booked at all. The biggest silent leak. */
  leadsWithNoNextStep: Type30.Integer(),
  /** Nobody got through, either direction, and nothing has happened since. */
  callsToReturn: Type30.Integer(),
  /** The analysis asked for a human ear and nobody has given it one. */
  flaggedCalls: Type30.Integer(),
  /** They wrote, and nobody replied. */
  unansweredMessages: Type30.Integer()
});
var WelcomePromise = Type30.Object({
  callId: Type30.Integer(),
  contactId: Type30.String({ format: "uuid" }),
  contactName: Type30.Union([Type30.String(), Type30.Null()]),
  contactPhone: Type30.Union([Type30.String(), Type30.Null()]),
  /** When the call happened — how old the promise is. */
  at: Type30.String({ format: "date-time" }),
  /** What was promised, in the model's words. */
  promised: Type30.String(),
  /**
   * Whether anything is scheduled with this customer since.
   *
   * The difference between "we owe them and nobody has planned it" and "we
   * owe them and it is in somebody's diary". Only the first is a problem, and
   * showing both without distinguishing them is how the list becomes noise.
   */
  covered: Type30.Boolean()
});
var ServiceStatus = Type30.Object({
  service: Type30.String(),
  /** Null when it has never pinged at all, which is not the same as down. */
  lastSeenAt: Type30.Union([Type30.String({ format: "date-time" }), Type30.Null()]),
  /** What it said about itself, when it said anything. */
  status: Type30.Union([Type30.String(), Type30.Null()]),
  detail: Type30.Union([Type30.String(), Type30.Null()]),
  /** False once the silence passes the threshold. Null means never seen. */
  healthy: Type30.Union([Type30.Boolean(), Type30.Null()]),
  /** How long it has been quiet, so the UI need not do date arithmetic. */
  secondsSinceSeen: Type30.Union([Type30.Integer(), Type30.Null()])
});
var YesterdayStats = Type30.Object({
  calls: Type30.Integer(),
  /** Of those, the ones somebody actually spoke on. */
  connected: Type30.Integer(),
  /** Closed yesterday, whatever day they were due — "I cleared eleven things". */
  followUpsDone: Type30.Integer(),
  newLeads: Type30.Integer(),
  /** Converted means they PAID. The one number here that is money. */
  converted: Type30.Integer()
});
var SupervisorPoint = Type30.Object({
  /** Stable, so the UI can pick an icon without parsing the text. */
  key: Type30.String(),
  /**
   * PRAISE COMES FIRST, and that ordering is the design rather than a nicety.
   *
   * This is the first screen somebody opens every morning. A supervisor who
   * leads with what went wrong every single day is one people stop listening
   * to by the second week, and the coaching underneath it goes unread with
   * them. So the page opens with what is going well and earns the right to
   * say the rest.
   *
   * NEITHER IS INVENTED. Praise is withheld when the numbers do not support
   * it — a page that congratulates everyone every day says nothing, and the
   * agent works out within a week that it is wallpaper.
   */
  tone: Type30.Union([Type30.Literal("praise"), Type30.Literal("improve"), Type30.Literal("note")]),
  /**
   * Where to go to act on it, when the point is about somebody specific.
   *
   * A BRIEFING IS ABOUT PEOPLE, not percentages. "48 leads have no next step"
   * is a workload; "Meenakshi Silks is hot and has been quiet nine days" is
   * something to do before coffee — and it is only that if the name is a link.
   */
  to: Type30.Optional(Type30.String()),
  /** The point itself, in a supervisor's voice. One sentence. */
  title: Type30.String(),
  /** What to do about it, or what the evidence is. One sentence. */
  detail: Type30.String()
});
var SUPERVISOR_LOOKBACK_DAYS = 14;
var SUPERVISOR_MIN_CALLS = 3;
var SUPERVISOR_MAX_POINTS = 6;
var SUPERVISOR_QUIET_HOT_DAYS = 3;
var SUPERVISOR_STRONG_SCORE = 8;
var SUPERVISOR_GOOD_SCORE = 75;
var SUPERVISOR_BALANCED_TALK = { min: 40, max: 65 };
var SUPERVISOR_WEAK_SCORE = 6.5;
var SUPERVISOR_TALK_RATIO = 70;
var BriefingFocus = Type30.Object({
  id: Type30.String(),
  priority: Type30.String(),
  category: Type30.String(),
  title: Type30.String(),
  description: Type30.String(),
  /** Who it is about, as the briefing named them. Free text, often a phone. */
  targetContact: Type30.Union([Type30.String(), Type30.Null()]),
  actionLabel: Type30.Union([Type30.String(), Type30.Null()]),
  /**
   * Where to go. VALIDATED BEFORE IT IS RENDERED as a link — the briefing is
   * model-written, and a route it invented leads somewhere that 404s.
   */
  actionRoute: Type30.Union([Type30.String(), Type30.Null()])
});
var DailyBriefing = Type30.Object({
  headline: Type30.Union([Type30.String(), Type30.Null()]),
  /** The supervisor's opening, in their voice. */
  directive: Type30.Union([Type30.String(), Type30.Null()]),
  focusPoints: Type30.Array(BriefingFocus)
});
var ClientCommitment = Type30.Object({
  id: Type30.String({ format: "uuid" }),
  contactId: Type30.Union([Type30.String({ format: "uuid" }), Type30.Null()]),
  contactName: Type30.Union([Type30.String(), Type30.Null()]),
  contactPhone: Type30.Union([Type30.String(), Type30.Null()]),
  commitmentText: Type30.Union([Type30.String(), Type30.Null()]),
  /** The specific thing to expect — "20-30 item inventory list & photos". */
  expectedDetails: Type30.Union([Type30.String(), Type30.Null()]),
  dueAt: Type30.Union([Type30.String({ format: "date-time" }), Type30.Null()]),
  nextBestAction: Type30.Union([Type30.String(), Type30.Null()]),
  /** Computed here, not stored: a stored one is wrong the moment it passes. */
  overdue: Type30.Boolean(),
  dueToday: Type30.Boolean()
});
var WelcomeResponse = Type30.Object({
  /** One instant for the whole page, so nothing on it disagrees. */
  generatedAt: Type30.String({ format: "date-time" }),
  /** Whose day this is, in their own clock. */
  agentName: Type30.Union([Type30.String(), Type30.Null()]),
  timezone: Type30.String(),
  yesterday: YesterdayStats,
  /** Empty when there are too few analysed calls to say anything honestly. */
  /** The written briefing, when one exists for today. Null falls back. */
  briefing: Type30.Union([DailyBriefing, Type30.Null()]),
  /** What customers said they would send us, soonest first. */
  clientCommitments: Type30.Array(ClientCommitment),
  supervisorPoints: Type30.Array(SupervisorPoint),
  /** How many analysed calls the points above were read from. */
  coachedCalls: Type30.Integer(),
  workload: WelcomeWorkload,
  promises: Type30.Array(WelcomePromise),
  services: Type30.Array(ServiceStatus)
});
var HEARTBEAT_STALE_SECONDS = 11 * 60;
var AI_SERVER_SERVICE = "ai_server";
var HeartbeatBody = Type30.Object({
  service: Type30.String({ minLength: 1, maxLength: 64 }),
  status: Type30.Optional(Type30.String({ maxLength: 32 })),
  detail: Type30.Optional(Type30.String({ maxLength: 500 }))
});

// src/whatsapp-cloud.ts
import { Type as Type31 } from "@sinclair/typebox";
var WHATSAPP_LANGUAGES = ["en", "en_US", "en_GB", "ta", "hi"];
var WHATSAPP_LANGUAGE_LABELS = {
  en: "English",
  en_US: "English (US)",
  en_GB: "English (UK)",
  ta: "Tamil",
  hi: "Hindi"
};
var MetaId = Type31.String({ minLength: 5, maxLength: 64 });
var AutoReplyConfig = Type31.Object({
  isEnabled: Type31.Boolean(),
  /**
   * Sent as free-form text, which is legal because it answers a message that
   * has just arrived. `{Name}` is substituted from the WhatsApp profile name.
   */
  body: Type31.String({ minLength: 1, maxLength: 4096 }),
  /** Put on its own line, with a link preview, so the reel renders as a card. */
  videoUrl: Type31.Union([Type31.String({ format: "uri", maxLength: 500 }), Type31.Null()]),
  /**
   * The approved template to fall back to when the 24-hour window has closed.
   *
   * Null is allowed and means "send nothing rather than something illegal".
   * An enquiry arriving from a form rather than from WhatsApp has never
   * messaged us, so there is no window open and no template is approved yet.
   */
  fallbackTemplateName: Type31.Union([Type31.String({ minLength: 1, maxLength: 512 }), Type31.Null()]),
  fallbackTemplateLanguage: enumOf(WHATSAPP_LANGUAGES)
});
var NURTURE_MAX_STEPS = 5;
var NurtureStepConfig = Type31.Object({
  /** Days after the sequence started, not after the previous step. */
  afterDays: Type31.Integer({ minimum: 1, maximum: 60 }),
  /** Must be an APPROVED template: every step is outside the 24-hour window. */
  templateName: Type31.String({ minLength: 1, maxLength: 512 }),
  templateLanguage: enumOf(WHATSAPP_LANGUAGES)
});
var NurtureConfig = Type31.Object({
  isEnabled: Type31.Boolean(),
  steps: Type31.Array(NurtureStepConfig, { maxItems: NURTURE_MAX_STEPS }),
  /**
   * Close the lead once every nudge has gone unanswered.
   *
   * The plan's "What NOT to automate" forbids a SILENT close: "a lead should
   * always get one visible final nudge before the CRM marks it dead". The
   * final step IS that nudge, so closing may only happen after it has been
   * sent — never instead of it. Enforced in the sweep, not here.
   */
  closeAfterFinalStep: Type31.Boolean(),
  /** Written into `lost_reason`, so a closed lead says why in plain words. */
  closeReason: Type31.String({ minLength: 1, maxLength: 300 })
});
var WhatsappCloudSettings = Type31.Object({
  isEnabled: Type31.Boolean(),
  /** Never the token itself. A hint, so an operator can tell which is installed. */
  hasAccessToken: Type31.Boolean(),
  tokenHint: Type31.Union([Type31.String(), Type31.Null()]),
  phoneNumberId: Type31.Union([Type31.String(), Type31.Null()]),
  wabaId: Type31.Union([Type31.String(), Type31.Null()]),
  /** Read back from Meta by the last check, so the page can show the number. */
  displayPhoneNumber: Type31.Union([Type31.String(), Type31.Null()]),
  verifiedName: Type31.Union([Type31.String(), Type31.Null()]),
  /** What to paste into Meta's webhook configuration. */
  webhookUrl: Type31.Union([Type31.String(), Type31.Null()]),
  /** Whether this DEPLOYMENT can verify a webhook at all — an env secret. */
  webhookConfigured: Type31.Boolean(),
  autoReply: AutoReplyConfig,
  nurture: NurtureConfig,
  lastCheckedAt: Type31.Union([Type31.String({ format: "date-time" }), Type31.Null()]),
  lastCheckOk: Type31.Union([Type31.Boolean(), Type31.Null()]),
  lastCheckMessage: Type31.Union([Type31.String(), Type31.Null()]),
  updatedAt: Type31.Union([Type31.String({ format: "date-time" }), Type31.Null()])
});
var UpdateWhatsappCloudBody = Type31.Partial(
  Type31.Object({
    isEnabled: Type31.Boolean(),
    accessToken: Type31.String({ maxLength: 1e3 }),
    phoneNumberId: MetaId,
    wabaId: MetaId,
    autoReply: Type31.Partial(AutoReplyConfig),
    nurture: Type31.Partial(NurtureConfig)
  })
);
var CheckWhatsappResponse = Type31.Object({
  ok: Type31.Boolean(),
  message: Type31.String(),
  displayPhoneNumber: Type31.Union([Type31.String(), Type31.Null()]),
  verifiedName: Type31.Union([Type31.String(), Type31.Null()])
});
var BULK_MESSAGE_MAX = 100;
var BulkMessageLeadsBody = Type31.Object({
  leadIds: Type31.Array(Type31.String({ format: "uuid" }), { minItems: 1, maxItems: BULK_MESSAGE_MAX }),
  /**
   * A template, always — never free-form text.
   *
   * A segment is by definition people who did not all message us in the last
   * 24 hours, so a text would be refused for most of them and the operator
   * would have no way to tell which. Requiring a template makes the rule
   * visible instead of making it a partial failure.
   */
  templateName: Type31.String({ minLength: 1, maxLength: 512 }),
  templateLanguage: enumOf(WHATSAPP_LANGUAGES)
});
var BulkMessageLeadsResponse = Type31.Object({
  queued: Type31.Integer(),
  /** No phone, do-not-contact, not visible to the caller, or Meta refused it. */
  skipped: Type31.Integer(),
  /** One line per lead that could not be messaged, so it can be fixed. */
  failures: Type31.Array(Type31.Object({ leadId: Type31.String({ format: "uuid" }), reason: Type31.String() }))
});
var DEFAULT_AUTO_REPLY = {
  isEnabled: false,
  body: [
    "\u0BB5\u0BA3\u0B95\u0BCD\u0B95\u0BAE\u0BCD {Name}! ePettyshop-\u0BB2 \u0B87\u0BB0\u0BC1\u0BA8\u0BCD\u0BA4\u0BC1 \u0BAA\u0BC7\u0B9A\u0BC1\u0BB1\u0BCB\u0BAE\u0BCD. \u{1F64F}",
    "",
    "\u0B89\u0B99\u0BCD\u0B95 \u0B95\u0B9F\u0BC8\u0B95\u0BCD\u0B95\u0BC1 online store, POS billing, payments, GST bill \u2014 \u0B8E\u0BB2\u0BCD\u0BB2\u0BBE\u0BAE\u0BCD \u0B92\u0BB0\u0BC7 \u0B87\u0B9F\u0BA4\u0BCD\u0BA4\u0BBF\u0BB2.",
    "\u0BAE\u0BBE\u0BA4\u0BAE\u0BCD \u20B9499-\u0BB2 \u0B87\u0BB0\u0BC1\u0BA8\u0BCD\u0BA4\u0BC1 \u0B86\u0BB0\u0BAE\u0BCD\u0BAA\u0BAE\u0BCD.",
    "",
    "\u0B87\u0BB0\u0BA3\u0BCD\u0B9F\u0BC1 \u0B95\u0BC7\u0BB3\u0BCD\u0BB5\u0BBF \u0BAE\u0B9F\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD:",
    "1. \u0B89\u0B99\u0BCD\u0B95 \u0B95\u0B9F\u0BC8 \u0BAA\u0BC6\u0BAF\u0BB0\u0BCD \u0B8E\u0BA9\u0BCD\u0BA9?",
    "2. \u0B8E\u0BA9\u0BCD\u0BA9 products \u0BB5\u0BBF\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC0\u0B99\u0BCD\u0B95?"
  ].join("\n"),
  videoUrl: null,
  fallbackTemplateName: null,
  fallbackTemplateLanguage: "ta"
};
var DEFAULT_NURTURE = {
  isEnabled: false,
  steps: [],
  closeAfterFinalStep: true,
  closeReason: "No reply after the full WhatsApp sequence"
};
var DEFAULT_NURTURE_DAYS = [1, 3, 7];

// src/whatsapp-import.ts
import { Type as Type32 } from "@sinclair/typebox";
var WHATSAPP_IMPORT_MAX_CHARS = 8e6;
var CHAT_DATE_ORDERS = ["dmy", "mdy"];
var WhatsappImportPreviewBody = Type32.Object({
  text: Type32.String({ minLength: 1, maxLength: WHATSAPP_IMPORT_MAX_CHARS }),
  /** Overrides the detected order. Only needed when `proven` came back false. */
  dateOrder: Type32.Optional(Type32.Union([Type32.Literal("dmy"), Type32.Literal("mdy")])),
  /** Minutes east of UTC for the exporting phone. Defaults to +330. */
  timezoneOffsetMinutes: Type32.Optional(Type32.Integer({ minimum: -840, maximum: 840 }))
});
var WhatsappImportPreview = Type32.Object({
  messageCount: Type32.Integer(),
  firstAt: Type32.Union([Type32.String({ format: "date-time" }), Type32.Null()]),
  lastAt: Type32.Union([Type32.String({ format: "date-time" }), Type32.Null()]),
  /**
   * Every name in the file with how much each said. The operator picks which
   * one is the business; nothing in the export says.
   */
  authors: Type32.Array(Type32.Object({ name: Type32.String(), messages: Type32.Integer() })),
  /** Lines the parser could not read. Shown, not hidden. */
  unparsedCount: Type32.Integer(),
  unparsedSample: Type32.Array(Type32.String()),
  /** System notices skipped on purpose — encryption banners and the like. */
  skippedCount: Type32.Integer(),
  dateOrder: Type32.Object({
    order: Type32.Union([Type32.Literal("dmy"), Type32.Literal("mdy")]),
    /**
     * False when every day in the file is 12 or under, so dd/mm and mm/dd
     * are indistinguishable. The UI must ask rather than let a silent guess
     * move the conversation by months.
     */
    proven: Type32.Boolean()
  }),
  /** How many of these the contact already has, so a re-import says so. */
  alreadyImported: Type32.Integer()
});
var WhatsappImportBody = Type32.Composite([
  WhatsappImportPreviewBody,
  Type32.Object({
    /**
     * The author name that is US. Everything else is treated as the
     * customer. Required and never inferred: getting it wrong reverses the
     * direction of every message in the history.
     */
    businessAuthor: Type32.String({ minLength: 1, maxLength: 200 })
  })
]);
var WhatsappImportResult = Type32.Object({
  imported: Type32.Integer(),
  /** Already present from an earlier import of the same conversation. */
  duplicates: Type32.Integer(),
  unparsedCount: Type32.Integer(),
  inbound: Type32.Integer(),
  outbound: Type32.Integer()
});
export {
  ACCOUNT_STATUSES,
  ACCOUNT_STATUS_LABELS,
  ACTIVITY_TYPES,
  AI_PROVIDERS,
  AI_PROVIDER_HINTS,
  AI_PROVIDER_LABELS,
  AI_SERVER_SERVICE,
  ANALYSE_CALL_RESULTS,
  ANALYSE_CALL_RESULT_MESSAGES,
  ANALYSE_CHAT_RESULTS,
  ANALYSE_CHAT_RESULT_MESSAGES,
  APP_USER_ROLES,
  APP_USER_ROLE_DESCRIPTIONS,
  APP_USER_ROLE_LABELS,
  ATTENTION_REASONS,
  ATTENTION_REASON_HINTS,
  ATTENTION_REASON_LABELS,
  ATTENTION_REASON_SHORT,
  ATTENTION_STALE_DAYS,
  ATTENTION_UNANSWERED_HOURS,
  AUDIBLE_NOTIFICATION_TYPES,
  Activity,
  ActivityPage,
  AddLeadTagBody,
  AiCoachScript,
  AnalyseCallBody,
  AnalyseCallResponse,
  AnalyseChatBody,
  AnalyseChatResponse,
  AttachManyBody,
  AttachManyResponse,
  AttachRequirementBody,
  AttentionGroup,
  AttentionLead,
  AutoReplyConfig,
  BEST_HOUR_MIN_CALLS,
  BRIEFING_PROMISE_DAYS,
  BRIEFING_PROMISE_LIMIT,
  BULK_CLOSE_MAX,
  BULK_MESSAGE_MAX,
  BULK_TAG_MAX,
  BestCallHour,
  BriefingDeal,
  BriefingFlags,
  BriefingFocus,
  BriefingLead,
  BulkCloseLeadsBody,
  BulkCloseLeadsResponse,
  BulkMessageLeadsBody,
  BulkMessageLeadsResponse,
  BulkTagLeadsBody,
  BulkTagLeadsResponse,
  CALLBACK_WINDOW_DAYS,
  CALLS_PAGE_MAX,
  CALL_ANALYSIS_STATUSES,
  CALL_ANALYSIS_STATUS_LABELS,
  CALL_DEMEANOURS,
  CALL_DEMEANOUR_HINTS,
  CALL_DEMEANOUR_LABELS,
  CALL_DIRECTIONS,
  CALL_DIRECTION_LABELS,
  CALL_DURATION_MAX_SECONDS,
  CALL_INTENTS,
  CALL_INTENT_LABELS,
  CALL_NOTE_MAX,
  CALL_OUTCOMES,
  CALL_OUTCOME_LABELS,
  CALL_REVIEW_REASONS,
  CALL_REVIEW_REASON_HINTS,
  CALL_REVIEW_REASON_LABELS,
  CALL_RISK_LEVELS,
  CALL_RISK_LEVEL_LABELS,
  CALL_SENTIMENTS,
  CALL_SENTIMENT_LABELS,
  CHAT_ANALYSIS_MESSAGE_WINDOW,
  CHAT_ANALYSIS_PROVIDER_DEFAULT,
  CHAT_DATE_ORDERS,
  CHAT_REANALYSE_AFTER_HOURS,
  CHAT_REANALYSE_NEW_MESSAGES,
  CONTACT_SORTS,
  CREATED_VIA,
  CURRENT_TEMPLATE_VARIABLES,
  CalendarLink,
  CalendarStatus,
  Call,
  CallAnalysis,
  CallHourStat,
  CallHourStatsResponse,
  CallMetrics,
  CallOutcomeCount,
  CallPage,
  CallQueuePage,
  CallQueueQuery,
  CampaignOption,
  ChangeDealStageBody,
  ChangeLeadStatusBody,
  ChatAnalysis,
  CheckWhatsappResponse,
  ClearCallReviewResponse,
  ClientCommitment,
  CoachedCall,
  CoachingPage,
  CoachingQuery,
  CompanyOption,
  CompleteFollowUpBody,
  ConnectCalendarBody,
  ConnectCalendarResponse,
  Contact,
  ContactBriefing,
  ContactPage,
  ConversationIntelligence,
  ConversationStats,
  ConvertLeadBody,
  ConvertLeadResponse,
  CreateCompanyBody,
  CreateContactBody,
  CreateDataSourceBody,
  CreateFollowUpBody,
  CreateLeadBody,
  CreateLeadResponse,
  CreateNoteBody,
  CreateRequirementBody,
  CreateTagBody,
  CreateTemplateBody,
  CreateUserBody,
  CrmUser,
  CustomerInsight,
  DASHBOARD_PERIODS,
  DASHBOARD_PERIOD_LABELS,
  DATA_SOURCE_AUTHS,
  DATA_SOURCE_AUTH_HINTS,
  DATA_SOURCE_AUTH_LABELS,
  DATA_SOURCE_FIELD_TYPES,
  DATA_SOURCE_FILTER_TYPES,
  DATA_SOURCE_KINDS,
  DEAL_BOARD_CARD_CAP,
  DEAL_SORTS,
  DEAL_SORT_LABELS,
  DEAL_STATUSES,
  DEAL_STATUS_LABELS,
  DEFAULT_AUTO_REPLY,
  DEFAULT_DIALLING_CODE,
  DEFAULT_NURTURE,
  DEFAULT_NURTURE_DAYS,
  DEFAULT_REMIND_BEFORE_MINUTES,
  DEFAULT_TARGET_METRIC,
  DEFAULT_TIMEZONE,
  DEFAULT_TIMEZONE_SHORT,
  DEPRECATED_TEMPLATE_VARIABLES,
  DIAL_RESULTS,
  DailyBriefing,
  DashboardDay,
  DashboardFollowUp,
  DashboardStats,
  DashboardStatsQuery,
  DataSource,
  DataSourceColumn,
  DataSourceDisplay,
  DataSourceFilter,
  DataSourceIdentity,
  DataSourceResult,
  DataSourceRow,
  DataSourceSummary,
  DateOnly,
  DaySlot,
  DaySlots,
  Deal,
  DealBoard,
  DealBoardQuery,
  DealBoardStage,
  DealListItem,
  DealPage,
  DialBody,
  DialResponse,
  DuplicateContactResponse,
  DuplicateRequirementResponse,
  DuplicateTagResponse,
  DuplicateTemplateResponse,
  ERROR_CODES,
  ErrorResponse,
  FOLLOW_UP_BUCKETS,
  FOLLOW_UP_OVERLAPPING_TYPES,
  FOLLOW_UP_PRIORITIES,
  FOLLOW_UP_PRIORITY_LABELS,
  FOLLOW_UP_SLOT_MINUTES,
  FOLLOW_UP_STATUSES,
  FOLLOW_UP_STATUS_LABELS,
  FOLLOW_UP_TYPES,
  FOLLOW_UP_TYPE_LABELS,
  FREJUN_EVENTS,
  FREJUN_EVENT_LABELS,
  FollowUp,
  FollowUpCalendar,
  FollowUpCalendarQuery,
  FollowUpCounts,
  FollowUpMetrics,
  FollowUpPage,
  FreeSlotsQuery,
  FrejunIngestResponse,
  GEMINI_AUTO_ACTION_CONFIDENCE_DEFAULT,
  GEMINI_DAILY_CHAT_CAP_DEFAULT,
  GEMINI_DAILY_MINUTE_CAP_DEFAULT,
  GEMINI_MIN_DURATION_SECONDS_DEFAULT,
  GEMINI_MODEL_DEFAULT,
  GeminiSettings,
  HEARTBEAT_STALE_SECONDS,
  HeartbeatBody,
  HowToOpen,
  INTEGRATION_PROVIDERS,
  IntegrationSettings,
  LEAD_LOST_REASONS,
  LEAD_LOST_REASON_LABELS,
  LEAD_NOTE_PREVIEW,
  LEAD_NOTE_PREVIEW_CHARS,
  LEAD_PRIORITIES,
  LEAD_PRIORITY_LABELS,
  LEAD_QUALITIES,
  LEAD_QUALITY_LABELS,
  LEAD_RATINGS,
  LEAD_RATING_LABELS,
  LEAD_SORTS,
  LEAD_SOURCES,
  LEAD_SOURCE_LABELS,
  LEAD_STATUSES,
  LEAD_STATUSES_CLOSED,
  LEAD_STATUSES_NEEDING_REASON,
  LEAD_STATUSES_SETTABLE,
  LEAD_STATUS_LABELS,
  LEAD_TYPES,
  LEAD_TYPE_LABELS,
  Lead,
  LeadArrival,
  LeadArrivalDay,
  LeadArrivals,
  LeadArrivalsQuery,
  LeadContactSummary,
  LeadMetrics,
  LeadNotePreview,
  LeadPage,
  LeadRequirement,
  LeadTag,
  ListActivitiesQuery,
  ListCallsQuery,
  ListContactsQuery,
  ListDealsQuery,
  ListFollowUpsQuery,
  ListLeadsQuery,
  ListNotesQuery,
  ListNotificationsQuery,
  ListRequirementCatalogQuery,
  ListRequirementLeadsQuery,
  ListTagsQuery,
  ListTargetsQuery,
  ListTemplatesQuery,
  ListUsersQuery,
  LogCallBody,
  LoginRequest,
  MY_DAY_FOLLOW_UP_CAP,
  MarkDealLostBody,
  MarkDealWonBody,
  MarkReadResponse,
  MeResponse,
  MonthOnly,
  MyDayQuery,
  MyDayResponse,
  MyOpenLeads,
  MyTargetResponse,
  NATIONAL_NUMBER_LENGTHS,
  NEXT_UP_DAYS,
  NOTES_PAGE_MAX,
  NOTE_BODY_MAX,
  NOTIFICATION_PANEL_LIMIT,
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABELS,
  NURTURE_MAX_STEPS,
  NeedsAttentionQuery,
  NeedsAttentionResponse,
  NewSeller,
  NewSellersQuery,
  NewSellersResponse,
  NextBestActionDetails,
  Note,
  Notification,
  NotificationPage,
  Nullable,
  NurtureConfig,
  NurtureStepConfig,
  OpenLeadCandidate,
  OpenLeadConflictResponse,
  PATTERN_HINTS,
  PHONE_E164_PATTERN,
  PHONE_PATTERN,
  PageQuery,
  PhoneE164,
  PhoneE164Input,
  PipelineStageOption,
  PipelineSummary,
  PromiseMade,
  QueryDataSourceBody,
  QueuedCall,
  REMIND_LEAD_LABELS,
  REMIND_LEAD_MINUTES,
  REQUIREMENT_ASKS,
  REQUIREMENT_ASK_LABELS,
  REQUIREMENT_DESCRIPTION_MAX,
  REQUIREMENT_DETAIL_MAX,
  REQUIREMENT_KINDS,
  REQUIREMENT_KIND_HINTS,
  REQUIREMENT_KIND_LABELS,
  REQUIREMENT_LIFECYCLES,
  REQUIREMENT_LIFECYCLE_LABELS,
  REQUIREMENT_NAME_MAX,
  REQUIREMENT_SORTS,
  REQUIREMENT_SORT_LABELS,
  RequirementCatalogEntry,
  RequirementCatalogPage,
  RequirementCounts,
  RequirementLeadPage,
  RequirementLeadRef,
  RescheduleFollowUpBody,
  SELLER_FLAGS,
  SELLER_FLAG_LABELS,
  SUPERVISOR_BALANCED_TALK,
  SUPERVISOR_GOOD_SCORE,
  SUPERVISOR_LOOKBACK_DAYS,
  SUPERVISOR_MAX_POINTS,
  SUPERVISOR_MIN_CALLS,
  SUPERVISOR_QUIET_HOT_DAYS,
  SUPERVISOR_STRONG_SCORE,
  SUPERVISOR_TALK_RATIO,
  SUPERVISOR_WEAK_SCORE,
  SUPPORT_NOTE_MAX,
  SaveIntegrationResponse,
  SearchLookupQuery,
  SendWhatsappBody,
  ServiceStatus,
  StaleLead,
  SupervisorPoint,
  TAG_COLOR_PATTERN,
  TAG_NAME_MAX,
  TARGET_METRICS,
  TARGET_METRIC_LABELS,
  TARGET_VALUE_MAX,
  TARGET_YEAR_BASES,
  TARGET_YEAR_BASIS_LABELS,
  TARGET_YEAR_START_MONTH,
  TEMPLATE_APPROVAL_STATUSES,
  TEMPLATE_NAME_MAX,
  TEMPLATE_VARIABLES,
  TEMPLATE_VARIABLE_GROUPS,
  TEMPLATE_VARIABLE_LABELS,
  TEMPLATE_VARIABLE_REPLACEMENTS,
  TIMEZONES,
  TIMEZONE_LABELS,
  TODAY_BLOCK_CAP,
  TODAY_BLOCK_MAX,
  TODAY_JOB_KINDS,
  TODAY_POOR_ANSWER_RATE,
  TODAY_SCOPES,
  TODAY_WORKING_HOURS,
  Tag,
  TagColor,
  TagOption,
  Target,
  TeamMemberPerformance,
  TeamPerformance,
  TeamPerformanceQuery,
  TeamPerformanceTotals,
  TemplatePage,
  TemplateUsage,
  TimezoneSchema,
  TodayBlock,
  TodayHour,
  TodayJob,
  TodayQuery,
  TodayResponse,
  UNREACHED_CALL_OUTCOMES,
  UnreadCount,
  UpdateCallBody,
  UpdateContactBody,
  UpdateDataSourceBody,
  UpdateDealBody,
  UpdateGeminiSettingsBody,
  UpdateIntegrationBody,
  UpdateLeadBody,
  UpdateLeadRequirementBody,
  UpdateMyTimezoneBody,
  UpdateNoteBody,
  UpdateRequirementBody,
  UpdateTargetBody,
  UpdateTemplateBody,
  UpdateUserBody,
  UpdateWhatsappCloudBody,
  UpsertTargetBody,
  UpsertTargetYearBody,
  UpsertTargetYearResponse,
  UserOption,
  WAITING_ON,
  WAITING_ON_LABELS,
  WA_LINK_MAX_LENGTH,
  WA_LINK_WARN_LENGTH,
  WHATSAPP_BODY_MAX,
  WHATSAPP_IMPORT_MAX_CHARS,
  WHATSAPP_LANGUAGES,
  WHATSAPP_LANGUAGE_LABELS,
  WHATSAPP_MESSAGE_TYPES,
  WHATSAPP_MESSAGE_TYPE_LABELS,
  WHATSAPP_SEND_METHODS,
  WHATSAPP_STATUSES,
  WHATSAPP_STATUS_LABELS,
  WebhookRegistration,
  WelcomePromise,
  WelcomeResponse,
  WelcomeWorkload,
  WhatsappCloudSettings,
  WhatsappImportBody,
  WhatsappImportPreview,
  WhatsappImportPreviewBody,
  WhatsappImportResult,
  WhatsappMessage,
  WhatsappMetrics,
  WhatsappTemplate,
  YesterdayStats,
  buildWaLink,
  enumOf,
  isDeprecatedTemplateVariable,
  isLeadClosed,
  isRequirementShipped,
  isValidE164,
  leadStatusNeedsReason,
  normalisePhone,
  occupiesSlot,
  pageOf,
  parseTemplateVariables,
  parseWhatsappPaste,
  renderTemplate,
  targetYearLabel,
  targetYearMonths,
  unknownTemplateVariables,
  unresolvedPlaceholders,
  waLinkLength,
  withDeprecatedAliases
};
